import type { Dict } from "../types";

export const reports: Dict = {
  en: {
    "Reports.depositsHeldTitle": "Deposits held",
    "Reports.depositsHeldDesc": "Deposits collected online and not yet applied to an invoice.",
    "Reports.depositsHeld.held": "Currently held",
    "Reports.depositsHeld.heldHint": "Owed to clients until the service is delivered.",
    "Reports.depositsHeld.heldAllTime": "Across all time, whatever period is shown above.",
    "Reports.depositsHeld.applied": "Applied to invoices",
    "Reports.depositsHeld.refunded": "Refunded",
    "Reports.depositsHeld.collected": "Collected over the period",
    "Reports.depositsHeld.liabilityNote": "A deposit is money held for a service not yet delivered, not revenue. Revenue is still recognised on the final invoice, where the deposit is applied.",
    "Reports.customerBalancesTitle": "Client balances",
    "Reports.customerBalancesDesc": "Amounts owed by clients, and the settlements of the period",
    "Reports.customerBalances.notRevenue": "This is not revenue. A partially paid invoice already counted its full sale when it was issued.",
    "Reports.customerBalances.outstanding": "Amounts owed as of today",
    "Reports.customerBalances.outstandingHint": "This figure is a snapshot as of today. It is not affected by the period selected above.",
    "Reports.customerBalances.periodMoves": "Movements over the period",
    "Reports.customerBalances.total": "Total",
    "Reports.customerBalances.customer": "Client",
    "Reports.customerBalances.amountOwed": "Amount owed",
    "Reports.customerBalances.since": "Since",
    "Reports.customerBalances.origin": "Origin",
    "Reports.customerBalances.sourceInvoice": "Invoice",
    "Reports.customerBalances.sourceManual": "Manual",
    "Reports.customerBalances.sourceMixed": "Mixed",
    "Reports.customerBalances.noOutstanding": "No client currently owes the salon anything.",
    "Reports.customerBalances.created": "Debts created",
    "Reports.customerBalances.settled": "Debts settled",
    "Reports.customerBalances.noMovements": "No movement over this period.",
    "Reports.customerBalances.date": "Date",
    "Reports.customerBalances.reason": "Reason",
    "Reports.customerBalances.method": "Method",
    "Reports.customerBalances.by": "By",
    "Reports.customerBalances.section": "Section",
    "Reports.customerBalances.reasonOrMethod": "Reason / method",
    "Reports.customerBalances.unknownCustomer": "Unidentified client",
    "Reports.customerBalances.splitCustomer": "Invoice split between several clients",
    "Reports.customerBalances.creditsOutstanding": "Credit notes outstanding",
    "Reports.customerBalances.creditsOutstandingHint": "Credit notes still spendable on client files today. This figure is a snapshot: it is not affected by the period selected above, and it is never subtracted from the amounts owed. It covers credit notes only, so it is not everything the salon owes its clients: gift cards are money owed too, and no report reads them yet.",
    "Reports.customerBalances.noCreditsOutstanding": "No client currently holds a spendable credit note.",
    "Reports.customerBalances.creditsGranted": "Credit notes granted over the period",
    "Reports.customerBalances.creditsSaleReversal": "Sale reversals",
    "Reports.customerBalances.creditsSaleReversalHint": "A sale that was returned and kept on the client file instead of being refunded in cash.",
    "Reports.customerBalances.creditsGoodwill": "Goodwill gestures",
    "Reports.customerBalances.creditsGoodwillHint": "Money given away, with no sale returned. This is a business expense, and this is where you read how much your team handed out.",
    "Reports.customerBalances.creditAmount": "Credit amount",
    "Reports.customerBalances.creditNature": "Nature",
    "Reports.customerBalances.creditNumber": "Credit note",
    "Reports.customerBalances.creditCancelled": "Cancelled",
    "Reports.customerBalances.natureSaleReversal": "Sale reversal",
    "Reports.customerBalances.natureGoodwill": "Goodwill",
    "Reports.customerBalances.natureMixed": "Mixed",
    "Reports.customerBalances.creditsHidden": "Credit notes are only visible with access to the Invoicing module.",
    "Reports.ownScope.emptyTitle": "No data for your account",
    "Reports.ownScope.emptyDesc": "Your account is limited to your own reports, but no provider profile is linked to it. Contact your salon owner.",

    // Reports - Selection Dialog
    "Reports.selection.title": "Select a Report",
    "Reports.selection.period": "Period: ",
    "Reports.selection.invoice": "invoice",
    "Reports.selection.invoices": "invoices",
    "Reports.selection.generateReport": "Generate report",
    "Reports.selection.financialTitle": "Financial Report",
    "Reports.selection.financialQuestion": "How is my business doing financially?",
    "Reports.selection.salesTitle": "Sales Report",
    "Reports.selection.salesQuestion": "What is selling and what is my product/service mix?",
    "Reports.selection.clientTitle": "Client Report",
    "Reports.selection.clientQuestion": "Who are my clients and are they loyal?",
    "Reports.selection.employeeTitle": "Employee Report",
    "Reports.selection.employeeQuestion": "How are my employees performing?",

    // Reports - Common
    "Reports.common.period": "Period: ",
    "Reports.common.invoice": "invoice",
    "Reports.common.invoices": "invoices",
    "Reports.common.executiveSummary": "Executive Summary",
    "Reports.common.generating": "Generating...",
    "Reports.common.category": "Category",
    "Reports.common.amount": "Amount",
    "Reports.common.type": "Type",
    "Reports.common.value": "Value",
    "Reports.common.service": "Service",
    "Reports.common.product": "Product",

    // Reports - Financial
    "Reports.financial.title": "Financial Report",
    "Reports.financial.totalCollected": "Total Collected",
    "Reports.financial.totalCollectedSub": "Total amount collected",
    "Reports.financial.netRevenue": "Net Revenue",
    "Reports.financial.netRevenueSub": "After discounts, before taxes",
    "Reports.financial.averageTicket": "Average Ticket",
    "Reports.financial.detailedSummary": "Detailed financial summary",
    "Reports.financial.percentOfTotal": "% of total collected",
    "Reports.financial.grossTotal": "Gross total",
    "Reports.financial.discounts": "Discounts",
    "Reports.financial.subtotal": "Subtotal",
    "Reports.financial.tps": "GST",
    "Reports.financial.tvq": "QST",
    "Reports.financial.tips": "Tips",
    "Reports.financial.netSalon": "Net salon (excl. tips)",
    "Reports.financial.totalCollectedRow": "Total collected",
    "Reports.financial.includeInvoices": "Include detailed invoices (Table B)",
    "Reports.financial.includeInvoicesDesc": "Adds a complete list of all invoices with line-by-line details",
    "Reports.financial.download": "Download financial report",
    "Reports.financial.paymentBreakdown": "Payment method breakdown",
    "Reports.financial.paymentMethod": "Payment method",
    "Reports.financial.creditCards": "Credit cards",
    "Reports.financial.giftCards": "Gift cards",
    "Reports.financial.cash": "Cash",
    "Reports.financial.debitCard": "Debit card",
    "Reports.financial.eTransfer": "E-transfer",
    "Reports.financial.check": "Check",
    "Reports.financial.other": "Other",
    "Reports.financial.transactions": "transactions",
    "Reports.financial.count": "Count",
    "Reports.financial.visa": "Visa",
    "Reports.financial.mastercard": "Mastercard",
    "Reports.financial.amex": "American Express",
    "Reports.financial.creditCardSubtotal": "Credit cards subtotal",

    // Reports - Sales
    "Reports.sales.title": "Sales Report",
    "Reports.sales.serviceRevenue": "Service Revenue",
    "Reports.sales.productRevenue": "Product Revenue",
    "Reports.sales.salesCount": "sales",
    "Reports.sales.serviceMix": "Service/Product Mix",
    "Reports.sales.salesBreakdown": "Sales breakdown",
    "Reports.sales.salesBreakdownSub": "Sales distribution",
    "Reports.sales.revenue": "Revenue",
    "Reports.sales.percentRevenue": "% of revenue",
    "Reports.sales.salesCountHeader": "Number of sales",
    "Reports.sales.avgTicket": "Average ticket",
    "Reports.sales.services": "Services",
    "Reports.sales.products": "Products",
    "Reports.sales.other": "Other",
    "Reports.sales.topServices": "Top performing services",
    "Reports.sales.totalRevenue": "Total revenue",
    "Reports.sales.salesHeader": "# sales",
    "Reports.sales.avgPrice": "Average price",
    "Reports.sales.topProducts": "Best selling products",
    "Reports.sales.quantitySold": "Quantity sold",
    "Reports.sales.revenueHeader": "Revenue",
    "Reports.sales.percentProductSales": "% of product sales",
    "Reports.sales.crossSell": "Cross-sell Analysis",
    "Reports.sales.indicator": "Indicator",
    "Reports.sales.serviceAndProduct": "Service + product invoices",
    "Reports.sales.serviceOnly": "Service only invoices",
    "Reports.sales.withProduct": "Invoices with product",
    "Reports.sales.attachmentRate": "Product attachment rate",
    "Reports.sales.download": "Download sales report",

    // Reports - Client
    "Reports.client.title": "Client Report",
    "Reports.client.uniqueClients": "Unique Clients",
    "Reports.client.overPeriod": "over the period",
    "Reports.client.recurringClients": "Recurring Clients",
    "Reports.client.ofClients": "of clients",
    "Reports.client.avgBasket": "Avg Basket/Client",
    "Reports.client.avgSpend": "Average spend",
    "Reports.client.distribution": "Client distribution",
    "Reports.client.clientType": "Client type",
    "Reports.client.count": "Count",
    "Reports.client.newClients": "New clients",
    "Reports.client.newClientsSub": "First invoice in the period",
    "Reports.client.recurringClientsSub": ">1 invoice in the period",
    "Reports.client.oneTimeClients": "One-time clients",
    "Reports.client.oneTimeClientsSub": "1 single invoice in the period",
    "Reports.client.topClients": "Top clients (actual value)",
    "Reports.client.client": "Client",
    "Reports.client.invoiceCount": "# Invoices",
    "Reports.client.totalSpent": "Total spent",
    "Reports.client.avgBasketHeader": "Average basket",
    "Reports.client.recurring": "recurring",
    "Reports.client.visitFrequency": "Visit frequency",
    "Reports.client.visits": "# visits",
    "Reports.client.lastVisit": "Last visit",
    "Reports.client.days": "days",
    "Reports.client.atRisk": "At risk",
    "Reports.client.atRiskNote": "At-risk clients haven't visited the salon in over 60 days",
    "Reports.client.download": "Download client report",

    // Reports - Employee
    "Reports.employee.title": "Employee Report",
    "Reports.employee.teamRevenue": "Team Total Revenue",
    "Reports.employee.teamRevenueSub": "All sales combined",
    "Reports.employee.teamAvgTicket": "Team Average Ticket",
    "Reports.employee.topPerformer": "Top performer",
    "Reports.employee.sectionCommission": "Section A: COMMISSION Report",
    "Reports.employee.paySummary": "Pay summary by employee",
    "Reports.employee.employee": "Employee",
    "Reports.employee.totalSales": "Total sales",
    "Reports.employee.commissionPercent": "% Commission",
    "Reports.employee.commissionAmount": "Commission $",
    "Reports.employee.tips": "Tips",
    "Reports.employee.totalToPay": "Total to pay",
    "Reports.employee.noCommission": "No commission",
    "Reports.employee.salesDetail": "Sales detail (pay justification)",
    "Reports.employee.sale": "sale",
    "Reports.employee.sales": "sales",
    "Reports.employee.date": "Date",
    "Reports.employee.serviceProduct": "Service / Product",
    "Reports.employee.commission": "Commission",
    "Reports.employee.commissionRequired": "Section A: Commission Report (configuration required)",
    "Reports.employee.noCommissionRates": "No commission rates configured for employees.",
    "Reports.employee.addCommissionRates": "Please add commission rates in the employee settings.",
    "Reports.employee.sectionPerformance": "Section B: PERFORMANCE Report (for monthly reviews)",
    "Reports.employee.globalPerformance": "Global employee performance",
    "Reports.employee.revenueGenerated": "Revenue generated",
    "Reports.employee.percentTotalRevenue": "% of total revenue",
    "Reports.employee.avgTicket": "Average ticket",
    "Reports.employee.servicesCount": "# Services",
    "Reports.employee.productsCount": "# Products",
    "Reports.employee.unassigned": "Unassigned",
    "Reports.employee.servicePerformance": "Performance by service",
    "Reports.employee.totalSalesHeader": "Total sales",
    "Reports.employee.servicesHeader": "# Services",
    "Reports.employee.avgPriceHeader": "Average price",
    "Reports.employee.percentSalonRevenue": "% of salon revenue",
    "Reports.employee.download": "Download employee report",
    "Reports.employee.sectionProductivity": "Section C: PRODUCTIVITY Metrics",
    "Reports.employee.productivityTable": "Productivity metrics by employee",
    "Reports.employee.workingDays": "Days",
    "Reports.employee.hoursWorked": "Hours",
    "Reports.employee.clientsPerDay": "Clients / Day",
    "Reports.employee.clientsPerHour": "Clients / Hour",
    "Reports.employee.servicesPerHour": "Services / Hour",
    "Reports.employee.servicesPerClient": "Services / Client",
    "Reports.employee.productsSoldPerClient": "Products / Client",
    "Reports.employee.rebookingRate": "% Pre-booking",
    "Reports.employee.notAvailable": "N/A",
    "Reports.employee.noAppointmentData": "No appointment data available for this period.",
    "Reports.downloadReport": "Download Report",

    // Reports Module
    "Reports.title": "Reports",
    "Reports.scrollLeft": "Scroll filters left",
    "Reports.scrollRight": "Scroll filters right",
    "Reports.subtitle": "Generate and download your business reports",
    "Reports.loading": "Loading...",
    "Reports.error": "An error occurred while loading this report. Please try again.",
    "Reports.selectDates": "Select Dates",
    "Reports.startDate": "Start Date",
    "Reports.endDate": "End Date",
    "Reports.selectStartDate": "Select a start date",
    "Reports.selectEndDate": "Select an end date",
    "Reports.continue": "Continue",
    "Reports.cancel": "Cancel",
    "Reports.back": "Back",
    "Reports.generating": "Generating...",
    "Reports.downloadPDF": "Download PDF",
    "Reports.exportCsv": "Export to CSV",
    "Reports.dateRequired": "Dates required",
    "Reports.dateRequiredDesc": "Please select a start and end date to generate the report.",
    "Reports.invalidDateRange": "Invalid date range",
    "Reports.invalidDateRangeDesc": "The start date must be before the end date.",
    "Reports.reportReady": "Report ready",
    "Reports.reportReadyDesc": "The report is ready for printing/download.",
    "Reports.errorGenerating": "Error generating report",
    "Reports.upgradeEnterprise": "Upgrade to Enterprise",
    "Reports.filterAll": "All reports",
    "Reports.filterSales": "Sales",
    "Reports.filterFinance": "Finance",
    "Reports.filterAppointments": "Appointments",
    "Reports.filterTeam": "Team",
    "Reports.filterClients": "Clients",
    "Reports.filterInventory": "Inventory",
    "Reports.noReportsInCategory": "No reports available in this category yet",
    "Reports.payrollPermissionNote": "Payroll and commission reports require the Payroll permission.",
    "Reports.comingSoon": "Coming soon",
    "Reports.comingSoonDesc": "This report is being developed and will be available soon.",
    "Reports.inDevelopment": "In development",
    "Reports.enterpriseRequired": "Enterprise Plan Required",
    "Reports.enterpriseRequiredDesc": "This report is available on the Enterprise plan.",
    "Reports.freshness.asOf": "Figures as of {date}",
    "Reports.freshness.refresh": "Refresh",
    "Reports.freshness.refreshing": "Refreshing…",
    "Reports.export.preparing": "Preparing…",
    "Reports.export.refreshFailed": "The figures could not be reloaded, so the file was not downloaded. Please try again.",
    "Reports.export.nothingToExport": "There is nothing to export for these filters.",
    "Reports.allMembers": "All team members",
    "Reports.allProducts": "All products",
    "Reports.allCategories": "All categories",
    "Reports.noCategory": "No category",
    "Reports.allSuppliers": "All suppliers",
    "Reports.noSupplier": "No supplier",
    "Reports.presetToday": "Today",
    "Reports.presetMonthToDate": "Month to date",
    "Reports.presetLast7Days": "Last 7 days",
    "Reports.presetLast30Days": "Last 30 days",
    "Reports.presetLastMonth": "Last month",
    "Reports.presetLast3Months": "Last 3 months",
    "Reports.presetLast6Months": "Last 6 months",
    "Reports.presetCustom": "Custom range",
    // Sales Summary report columns & labels
    "Reports.salesSummary.type": "Type",
    "Reports.salesSummary.article": "Article",
    "Reports.salesSummary.qtySold": "Qty sold",
    "Reports.salesSummary.grossSales": "Gross sales",
    "Reports.salesSummary.totalDiscounts": "Total discounts",
    "Reports.salesSummary.refunds": "Refunds",
    "Reports.salesSummary.netSales": "Net sales",
    "Reports.salesSummary.taxes": "Taxes",
    "Reports.salesSummary.totalSales": "Total sales",
    "Reports.salesSummary.total": "Total",
    "Reports.salesSummary.service": "Service",
    "Reports.salesSummary.product": "Product",
    "Reports.salesSummary.other": "Other",
    "Reports.salesSummary.noData": "No sales data for this period",
    "Reports.salesSummary.backToTypes": "Back to types",
    // Sales List report columns & labels
    "Reports.salesList.saleNumber": "Sale #",
    "Reports.salesList.saleDate": "Sale date",
    "Reports.salesList.status": "Status",
    "Reports.salesList.client": "Client",
    "Reports.salesList.itemsSold": "Items sold",
    "Reports.salesList.totalSales": "Total sales",
    "Reports.salesList.giftCard": "Gift card",
    "Reports.salesList.autonomous": "Self-employed",
    "Reports.salesList.amountDue": "Amount due",
    "Reports.salesList.total": "Total",
    "Reports.salesList.noData": "No sales for this period",
    "Reports.salesList.showing": "Showing",
    "Reports.salesList.of": "of",
    "Reports.salesList.results": "results",
    // Sales Detailed Journal
    "Reports.salesDetailedJournal.date": "Date",
    "Reports.salesDetailedJournal.saleNumber": "Sale #",
    "Reports.salesDetailedJournal.type": "Type",
    "Reports.salesDetailedJournal.item": "Item",
    "Reports.salesDetailedJournal.section": "Section",
    "Reports.salesDetailedJournal.client": "Client",
    "Reports.salesDetailedJournal.teamMember": "Team member",
    "Reports.salesDetailedJournal.grossSales": "Gross sales",
    "Reports.salesDetailedJournal.itemDiscounts": "Item discounts",
    "Reports.salesDetailedJournal.cartDiscounts": "Cart discounts",
    "Reports.salesDetailedJournal.totalDiscounts": "Total discounts",
    "Reports.salesDetailedJournal.refunds": "Refunds",
    "Reports.salesDetailedJournal.netSales": "Net sales",
    "Reports.salesDetailedJournal.netSalesTaxes": "Net sales taxes",
    "Reports.salesDetailedJournal.totalSales": "Total sales",
    "Reports.salesDetailedJournal.total": "Total",
    "Reports.salesDetailedJournal.noData": "No sales for this period",
    "Reports.salesDetailedJournal.showing": "Showing",
    "Reports.salesDetailedJournal.of": "of",
    "Reports.salesDetailedJournal.results": "results",
    "Reports.salesDetailedJournal.na": "N/A",
    "Reports.salesDetailedJournal.refundOf": "Refund of invoice",

    // Discount Summary
    "Reports.discountSummary.discountCategory": "Discount category",
    "Reports.discountSummary.discountedItems": "Discounted items",
    "Reports.discountSummary.grossSales": "Gross sales",
    "Reports.discountSummary.itemDiscounts": "Item discounts",
    "Reports.discountSummary.cartDiscounts": "Cart discounts",
    "Reports.discountSummary.totalDiscounts": "Total discounts",
    "Reports.discountSummary.totalDiscountPercent": "Discount %",
    "Reports.discountSummary.total": "Total",
    "Reports.discountSummary.noData": "No discounts applied in this period",
    "Reports.discountSummary.manualDiscount": "Manual discount",
    "Reports.taxSummary.taxType": "Tax type",
    "Reports.taxSummary.taxRate": "Tax rate",
    "Reports.taxSummary.itemsSold": "Items sold",
    "Reports.taxSummary.billedTaxes": "Taxes billed",
    "Reports.taxSummary.adjustments": "Period adjustments",
    "Reports.taxSummary.netTaxes": "Net taxes",
    "Reports.taxSummary.total": "Total",
    "Reports.taxSummary.noData": "No taxes found for this period.",
    "Reports.taxSummary.byCategory.service": "Services",
    "Reports.taxSummary.byCategory.product": "Products",
    "Reports.taxSummary.byCategory.other": "Other",

    "Reports.financialSummary.sales": "Sales",
    "Reports.financialSummary.grossSales": "Gross sales",
    "Reports.financialSummary.discounts": "Discounts",
    "Reports.financialSummary.refunds": "Refunds/returns",
    "Reports.financialSummary.netSales": "Net sales",
    "Reports.financialSummary.taxes": "Taxes",
    "Reports.financialSummary.totalSales": "Total sales",
    "Reports.financialSummary.giftCardSales": "Gift card sales",
    "Reports.financialSummary.tips": "Tips",
    "Reports.financialSummary.greencircleFees": "Green Circle fees",
    "Reports.financialSummary.totalOtherSales": "Total other sales",
    "Reports.financialSummary.totalSalesPlusOther": "Total sales + other sales",
    "Reports.financialSummary.paymentsByPaidDate": "Payments (by payment date)",
    "Reports.financialSummary.totalPayments": "Total payments",
    "Reports.financialSummary.noData": "No financial data found for this period.",

    "Reports.paymentsSummary.paymentMethod": "Payment method",
    "Reports.paymentsSummary.paymentCount": "No. of payments",
    "Reports.paymentsSummary.amount": "Amount",
    "Reports.paymentsSummary.refundCount": "No. of refunds",
    "Reports.paymentsSummary.refunds": "Refunds",
    "Reports.paymentsSummary.netPayments": "Net payments",
    "Reports.paymentsSummary.total": "Total",
    "Reports.paymentsSummary.noData": "No payments found for this period.",

    "Reports.giftCardsList.code": "Code",
    "Reports.giftCardsList.saleNumber": "Sale #",
    "Reports.giftCardsList.purchasedBy": "Purchased by",
    "Reports.giftCardsList.status": "Status",
    "Reports.giftCardsList.issueDate": "Issue date",
    "Reports.giftCardsList.expirationDate": "Expiration date",
    "Reports.giftCardsList.na": "N/A",
    "Reports.giftCardsList.total": "Total",
    "Reports.giftCardsList.giftCards": "gift cards",
    "Reports.giftCardsList.noData": "No gift cards found for this period.",
    "Reports.giftCardsList.showing": "Showing",
    "Reports.giftCardsList.of": "of",
    "Reports.giftCardsList.results": "results",
    "Reports.giftCardsList.unknownCustomer": "Unknown customer",
    "Reports.itemType.service": "Service",
    "Reports.itemType.product": "Product",
    "Reports.itemType.tip": "Tip",
    "Reports.itemType.discount": "Discount",
    "Reports.itemType.gift_card_sale": "Gift card",
    "Reports.itemType.gift_card_topup": "Gift card top-up",
    "Reports.itemType.gift_card_redemption": "Gift card redemption",
    "Reports.itemType.other": "Other",
    // Sales reports
    "Reports.salesSummaryTitle": "Sales Summary",
    "Reports.salesSummaryDesc": "Quantity and amount of sales, excluding tips and gift card sales.",
    "Reports.salesByPeriodTitle": "Sales by Period",
    "Reports.salesByPeriodDesc": "Detailed sales data based on selected periods.",
    "Reports.salesByPeriod.noData": "No sales data for this period",
    "Reports.salesByPeriod.caHt": "Net sales",
    "Reports.salesByPeriod.tps": "GST",
    "Reports.salesByPeriod.tvq": "QST",
    "Reports.salesByPeriod.caTtc": "Total sales",
    "Reports.salesByPeriod.invoiceCount": "# invoices",
    "Reports.salesByPeriod.avgTicket": "Avg ticket",
    "Reports.salesByPeriod.refunds": "Refunds, taxes included",
    "Reports.salesByPeriod.byCategory": "By category",
    "Reports.salesByPeriod.byPaymentMethodPaidDate": "By payment method (by payment date)",
    "Reports.salesByPeriod.byDay": "Daily breakdown",
    "Reports.salesByPeriod.topEmployees": "Top sellers",
    "Reports.salesByPeriod.category.service": "Services",
    "Reports.salesByPeriod.category.product": "Products",
    "Reports.salesByPeriod.category.tip": "Tips",
    "Reports.salesByPeriod.category.gift_card": "Gift cards",
    "Reports.salesByPeriod.category.discount": "Discounts",
    "Reports.salesByPeriod.category.other": "Other",
    "Reports.salesByPeriod.column.category": "Category",
    "Reports.salesByPeriod.column.amount": "Amount",
    "Reports.salesByPeriod.column.percent": "%",
    "Reports.salesByPeriod.column.method": "Method",
    "Reports.salesByPeriod.column.date": "Date",
    "Reports.salesByPeriod.column.invoiceCount": "# invoices",
    "Reports.salesByPeriod.column.netHt": "Net sales",
    "Reports.salesByPeriod.column.tps": "GST",
    "Reports.salesByPeriod.column.tvq": "QST",
    "Reports.salesByPeriod.column.totalTtc": "Total sales",
    "Reports.salesByPeriod.column.rank": "Rank",
    "Reports.salesByPeriod.column.employee": "Employee",
    "Reports.salesListTitle": "Sales List",
    "Reports.salesListDesc": "Complete list of all sales transactions.",
    "Reports.salesDetailedJournalTitle": "Detailed Sales Journal",
    "Reports.salesDetailedJournalDesc": "Detailed view of each sales transaction.",
    "Reports.giftCardsByPeriodTitle": "Gift Cards by Time Period",
    "Reports.giftCardsByPeriodDesc": "Gift card sales and usage data based on selected time periods.",
    "Reports.giftCardsByPeriod.noData": "No gift card activity for this period",
    "Reports.giftCardsByPeriod.issuedAmount": "Issued",
    "Reports.giftCardsByPeriod.usedAmount": "Used",
    "Reports.giftCardsByPeriod.inCirculationAmount": "In circulation",
    "Reports.giftCardsByPeriod.expiredAmount": "Expired",
    "Reports.giftCardsByPeriod.cards": "cards",
    "Reports.giftCardsByPeriod.issuance": "Issuance",
    "Reports.giftCardsByPeriod.usage": "Usage",
    "Reports.giftCardsByPeriod.inCirculation": "In circulation",
    "Reports.giftCardsByPeriod.column.date": "Date",
    "Reports.giftCardsByPeriod.column.code": "Code",
    "Reports.giftCardsByPeriod.column.customer": "Customer",
    "Reports.giftCardsByPeriod.column.initialAmount": "Initial amount",
    "Reports.giftCardsByPeriod.column.expiration": "Expiration",
    "Reports.giftCardsByPeriod.column.sellingEmployee": "Sold by",
    "Reports.giftCardsByPeriod.column.amount": "Amount used",
    "Reports.giftCardsByPeriod.column.invoice": "Invoice",
    "Reports.giftCardsByPeriod.column.employee": "Employee",
    "Reports.giftCardsByPeriod.column.currentBalance": "Current balance",
    "Reports.giftCardsByPeriod.column.issueDate": "Issue date",
    "Reports.giftCardsListTitle": "Gift Cards List",
    "Reports.giftCardsListDesc": "Complete list of issued and unused gift cards.",
    "Reports.subscriptionsListTitle": "Subscriptions List",
    "Reports.subscriptionsListDesc": "Complete list of active subscriptions.",
    "Reports.discountsSummaryTitle": "Discounts Summary",
    "Reports.discountsSummaryDesc": "Overview of discounts applied and their impact on sales.",
    "Reports.salesAnalysisTitle": "Cross-sell Analysis",
    "Reports.salesAnalysisDesc": "Product attachment rate and invoice composition by type.",
    "Reports.salesAnalysis.invoiceType": "Invoice type",
    "Reports.salesAnalysis.invoiceCount": "# invoices",
    "Reports.salesAnalysis.percentInvoices": "% of invoices",
    "Reports.salesAnalysis.revenue": "Revenue",
    "Reports.salesAnalysis.percentRevenue": "% of revenue",
    "Reports.salesAnalysis.avgTicket": "Average ticket",
    "Reports.salesAnalysis.other": "Other",
    "Reports.salesAnalysis.total": "Total",
    "Reports.salesAnalysis.serviceAndProduct": "Service + product",
    "Reports.salesAnalysis.serviceOnly": "Service only",
    "Reports.salesAnalysis.withProduct": "Product only",
    "Reports.salesAnalysis.attachmentRate": "Product attachment rate",
    "Reports.salesAnalysis.noData": "No sales data for this period.",
    "Reports.taxesSummaryTitle": "Taxes Summary",
    "Reports.taxesSummaryDesc": "Summary of all tax-related transactions.",
    // Finance reports
    "Reports.financialSummaryTitle": "Financial Summary",
    "Reports.financialSummaryDesc": "Comprehensive summary of sales, payments, and liabilities.",
    "Reports.paymentsSummaryTitle": "Payments Summary",
    "Reports.paymentsSummaryDesc": "Payments broken down by payment methods.",
    "Reports.financialTransactionsTitle": "Financial Transactions",
    "Reports.financialTransactionsDesc": "Detailed overview of all payment operations.",
    "Reports.financialTransactions.paymentDate": "Payment date",
    "Reports.financialTransactions.saleDate": "Sale date",
    "Reports.financialTransactions.saleNumber": "Sale #",
    "Reports.financialTransactions.client": "Client",
    "Reports.financialTransactions.teamMember": "Team member",
    "Reports.financialTransactions.transactionType": "Transaction type",
    "Reports.financialTransactions.paymentMethod": "Payment method",
    "Reports.financialTransactions.externallySettled": "Settled outside Flowcut",
    "Reports.financialTransactions.amount": "Amount",
    "Reports.financialTransactions.total": "Total",
    "Reports.totalLabel": "Total:",
    "Reports.financialTransactions.transactions": "transactions",
    "Reports.financialTransactions.noData": "No transactions for this period.",
    "Reports.financialTransactions.showing": "Showing",
    "Reports.financialTransactions.of": "of",
    "Reports.financialTransactions.results": "results",
    "Reports.taxList.saleNumber": "Sale #",
    "Reports.taxList.saleDate": "Sale date",
    "Reports.taxList.type": "Type",
    "Reports.taxList.item": "Item",
    "Reports.taxList.category": "Category",
    "Reports.taxList.teamMember": "Team member",
    "Reports.taxList.client": "Client",
    "Reports.taxList.paymentMethod": "Payment method",
    "Reports.taxList.netSales": "Net sales",
    "Reports.taxList.taxName": "Tax name",
    "Reports.taxList.taxRate": "Tax rate",
    "Reports.taxList.taxOnNetSales": "Tax on net sales",
    "Reports.taxList.total": "Total",
    "Reports.taxList.rows": "rows",
    "Reports.taxList.noData": "No tax transactions for this period.",
    "Reports.taxList.showing": "Showing",
    "Reports.taxList.of": "of",
    "Reports.taxList.results": "results",
    "Reports.taxList.refundOf": "Refund of invoice",
    "Reports.taxList.typeRefund": "Refund",
    "Reports.taxList.typeChargeback": "Chargeback",
    "Reports.cashFlowSummaryTitle": "Cash Flow Summary",
    "Reports.cashFlowSummaryDesc": "Overview of cash inflows and outflows.",
    "Reports.cashFlowStatementTitle": "Cash Flow Statement",
    "Reports.cashFlowStatementDesc": "Detailed cash flow journal over a selected period.",
    "Reports.serviceFeesTitle": "Service Fees",
    "Reports.serviceFeesDesc": "Breakdown of service fee revenue.",
    "Reports.liabilitiesSummaryTitle": "Liabilities Summary",
    "Reports.liabilitiesSummaryDesc": "Overview of business liabilities by type. Excludes unpaid and cancelled gift cards.",
    "Reports.liabilityActivitiesTitle": "Liability Activities",
    "Reports.liabilityActivitiesDesc": "Detailed overview of liability-related operations.",
    "Reports.prepaymentsByPeriodTitle": "Prepayments by Time Period",
    "Reports.prepaymentsByPeriodDesc": "Analysis of prepayments over a selected time period.",
    "Reports.prepaymentListTitle": "Prepayment List",
    "Reports.prepaymentListDesc": "Complete record of all prepayments.",
    "Reports.taxesListTitle": "Taxes List",
    "Reports.taxesListDesc": "Complete list of all transactions containing taxes.",
    "Reports.greencircleTitle": "Green Circle report",
    "Reports.greencircleDesc": "Total Green Circle fees charged during the selected period.",
    "Reports.greencircleNoData": "No Green Circle fees for this period",
    "Reports.greencircleDate": "Date",
    "Reports.greencircleInvoiceCount": "Number of invoices",
    "Reports.greencircleFee": "Fee",
    "Reports.greencircleTotal": "Total Green Circle fees",
    // Appointment reports
    "Reports.appointmentsSummaryTitle": "Appointments Summary",
    "Reports.appointmentsSummaryDesc": "General overview of appointment patterns and trends, including cancellations and no-shows.",
    "Reports.apptSummary.member": "Team member",
    "Reports.apptSummary.appointments": "Appointments",
    "Reports.apptSummary.services": "Services",
    "Reports.apptSummary.totalAmount": "Total amount",
    "Reports.apptSummary.avgAmount": "Avg. amount",
    "Reports.apptSummary.onlinePercent": "% online",
    "Reports.apptSummary.cancelledPercent": "% cancelled",
    "Reports.apptSummary.noShowPercent": "% no-show",
    "Reports.apptSummary.totalClients": "Total clients",
    "Reports.apptSummary.newClients": "New clients",
    "Reports.apptSummary.newClientPercent": "% new clients",
    "Reports.apptSummary.recurringPercent": "% recurring",
    "Reports.apptSummary.total": "Total",
    "Reports.apptSummary.noData": "No appointment data for this period.",
    "Reports.apptList.ref": "Ref.",
    "Reports.apptList.client": "Client",
    "Reports.apptList.teamMember": "Team member",
    "Reports.apptList.resource": "Resource",
    "Reports.apptList.status": "Status",
    "Reports.apptList.createdDate": "Created on",
    "Reports.apptList.scheduledDate": "Scheduled date",
    "Reports.apptList.cancelledDate": "Cancelled on",
    "Reports.apptList.category": "Category",
    "Reports.apptList.service": "Service",
    "Reports.apptList.duration": "Duration (min)",
    "Reports.apptList.timeSlot": "Time slot",
    "Reports.apptList.createdBy": "Created by",
    "Reports.apptList.cancelledBy": "Cancelled by",
    "Reports.apptList.cancellationReason": "Cancellation reason",
    "Reports.apptList.netSales": "Net sales",
    "Reports.apptList.total": "Total",
    "Reports.apptList.noData": "No appointments for this period.",
    "Reports.apptList.salon": "Salon",
    "Reports.apptList.online": "Online",
    "Reports.apptList.voiceAI": "Voice AI",
    "Reports.apptList.statusConfirmed": "Confirmed",
    "Reports.apptList.statusCompleted": "Completed",
    "Reports.apptList.statusCancelled": "Cancelled",
    "Reports.apptList.statusNoShow": "No-show",
    "Reports.apptList.unassigned": "Unassigned",
    "Reports.appointmentsListTitle": "Appointments List",
    "Reports.appointmentsListDesc": "Complete list of scheduled appointments.",
    "Reports.cancelledMissedTitle": "Cancelled & Missed Summary",
    "Reports.cancelledMissedDesc": "Data on cancelled and missed appointments.",
    "Reports.voiceAiChannelTitle": "Voice AI bookings",
    "Reports.voiceAiChannelDesc": "Bookings, reschedules, cancellations and call quality from the AI voice assistant.",
    "Reports.voiceAiChannel.bookings": "AI bookings",
    "Reports.voiceAiChannel.reschedules": "AI reschedules",
    "Reports.voiceAiChannel.cancellations": "AI cancellations",
    "Reports.voiceAiChannel.totalCalls": "Total calls",
    "Reports.voiceAiChannel.successRate": "Success rate",
    "Reports.voiceAiChannel.avgDuration": "Average duration",
    "Reports.voiceAiChannel.bookingsSection": "Bookings",
    "Reports.voiceAiChannel.callsSection": "Calls",
    "Reports.voiceAiChannel.noData": "No AI data for this period",
    "Reports.voiceAiChannel.csvMetric": "Metric",
    "Reports.voiceAiChannel.csvValue": "Total",
    "Reports.directBookingChannelTitle": "Direct booking (QR + Link)",
    "Reports.directBookingChannelDesc": "QR/link visits and conversion rate into appointments.",
    "Reports.directBookingChannel.totalVisits": "Direct visits (all sources)",
    "Reports.directBookingChannel.conversions": "Conversions",
    "Reports.directBookingChannel.conversionRate": "Conversion rate",
    "Reports.directBookingChannel.noData": "No direct visits for this period",
    "Reports.directBookingChannel.csvMetric": "Metric",
    "Reports.directBookingChannel.csvValue": "Total",
    "Reports.cancelledMissed.reason": "Reason",
    "Reports.cancelledMissed.count": "Appointments",
    "Reports.cancelledMissed.percent": "% of total",
    "Reports.cancelledMissed.amount": "Amount",
    "Reports.cancelledMissed.total": "Total",
    "Reports.cancelledMissed.noData": "No cancelled or missed appointments for this period.",
    "Reports.cancelledMissed.byClient": "Cancelled by client",
    "Reports.cancelledMissed.bySalon": "Cancelled by salon",
    "Reports.cancelledMissed.byVoiceAi": "Cancelled by voice AI",
    "Reports.cancelledMissed.noReason": "Cancelled (no reason)",
    "Reports.cancelledMissed.noShow": "No-show",
    "Reports.waitlistDetailsTitle": "Waitlist Details",
    "Reports.waitlistDetailsDesc": "Detailed view of clients on the waitlist.",
    "Reports.waitlistSummaryTitle": "Waitlist Summary",
    "Reports.waitlistSummaryDesc": "Overview of waitlist trends and patterns, including booked and expired entries.",
    // Team reports
    "Reports.workHoursActivityTitle": "Work Hours Activity",
    "Reports.workHoursActivityDesc": "Detailed view of hours worked, time slots, and timesheets.",
    "Reports.breakActivityTitle": "Break Activity",
    "Reports.breakActivityDesc": "Detailed view of team member breaks.",
    "Reports.attendanceSummaryTitle": "Attendance Summary",
    "Reports.attendanceSummaryDesc": "Overview of team member punctuality and attendance for their time slots.",
    "Reports.salaryDetailsTitle": "Salary Details",
    "Reports.salaryDetailsDesc": "Detailed overview of salaries paid to team members across all locations.",
    "Reports.salarySummaryTitle": "Salary Summary",
    "Reports.salarySummaryDesc": "Overview of salaries earned by team members.",
    "Reports.feeDeductionsHistoryTitle": "Fee Deductions History",
    "Reports.feeDeductionsHistoryDesc": "Complete list of fees deducted from team member compensation.",
    "Reports.feeDeductionsSummaryTitle": "Fee Deductions Summary",
    "Reports.feeDeductionsSummaryDesc": "Overview of fees deducted from compensation by team member, location, and item sold.",
    "Reports.payrollSummaryTitle": "Payroll Summary",
    "Reports.payrollSummaryDesc": "Overview of team member compensation.",
    "Reports.payrollSummary.noData": "No finalized payroll found for this period. Finalize payroll runs in the Payroll module to see data here.",
    "Reports.payrollSummary.overview": "Overview",
    "Reports.payrollSummary.revenue": "Revenue",
    "Reports.payrollSummary.salaries": "Salaries",
    "Reports.payrollSummary.commissions": "Commissions",
    "Reports.payrollSummary.tips": "Tips",
    "Reports.payrollSummary.deductions": "Deductions",
    "Reports.payrollSummary.totalPay": "Total Pay",
    "Reports.payrollSummary.salariesSection": "Salaries",
    "Reports.payrollSummary.hourlyPay": "Hourly pay",
    "Reports.payrollSummary.hourlyRate": "Hourly rate",
    "Reports.payrollSummary.regularHoursPaid": "Regular hours paid",
    "Reports.payrollSummary.overtimePay": "Overtime pay",
    "Reports.payrollSummary.overtimeRate": "Overtime rate",
    "Reports.payrollSummary.overtimeHoursPaid": "Overtime hours paid",
    "Reports.payrollSummary.totalPaidHoursWorked": "Total paid hours worked",
    "Reports.payrollSummary.fixedSalary": "Fixed salary",
    "Reports.payrollSummary.totalSalaries": "Total Salaries",
    "Reports.payrollSummary.commissionsSection": "Commissions",
    "Reports.payrollSummary.serviceRevenue": "Service revenue",
    "Reports.payrollSummary.serviceCommission": "Service commission",
    "Reports.payrollSummary.productRevenue": "Product revenue",
    "Reports.payrollSummary.productCommission": "Product commission",
    "Reports.payrollSummary.totalCommissions": "Total Commissions",
    "Reports.payrollSummary.tipsSection": "Tips",
    "Reports.payrollSummary.totalTips": "Total Tips",
    "Reports.payrollSummary.deductionsSection": "Deductions",
    "Reports.payrollSummary.dailyDeductions": "Daily deductions",
    "Reports.payrollSummary.namedDeductions": "Named deductions",
    "Reports.payrollSummary.revenuePercentDeduction": "Revenue % deduction",
    "Reports.payrollSummary.totalDeductions": "Total Deductions",
    "Reports.payrollSummary.total": "Total",
    "Reports.payrollSummary.grandTotal": "Grand Total",
    "Reports.scheduledShiftsTitle": "Scheduled Shifts",
    "Reports.scheduledShiftsDesc": "Detailed display of scheduled team member time slots.",
    "Reports.scheduledShifts.noData": "No scheduled shifts found for this period.",
    "Reports.scheduledShifts.teamMember": "Team member",
    "Reports.scheduledShifts.establishment": "Establishment",
    "Reports.scheduledShifts.dayOfWeek": "Day of week",
    "Reports.scheduledShifts.date": "Date",
    "Reports.scheduledShifts.scheduledStart": "Scheduled start",
    "Reports.scheduledShifts.scheduledEnd": "Scheduled end",
    "Reports.scheduledShifts.duration": "Duration",
    "Reports.scheduledShifts.total": "Total",
    "Reports.hoursWorkedSummaryTitle": "Hours Worked Summary",
    "Reports.hoursWorkedSummaryDesc": "Overview of hours worked and productivity.",
    "Reports.hoursWorkedSummary.teamMember": "Team member",
    "Reports.hoursWorkedSummary.scheduled": "Scheduled",
    "Reports.hoursWorkedSummary.blocked": "Blocked",
    "Reports.hoursWorkedSummary.available": "Available",
    "Reports.hoursWorkedSummary.booked": "Booked",
    "Reports.hoursWorkedSummary.notBooked": "Not booked",
    "Reports.hoursWorkedSummary.occupancy": "Occupancy %",
    "Reports.hoursWorkedSummary.total": "Total",
    "Reports.hoursWorkedSummary.noData": "No hours worked data for this period.",
    "Reports.teamAbsenceSummaryTitle": "Team Absence Summary",
    "Reports.teamAbsenceSummaryDesc": "Detailed overview of team member time off.",
    "Reports.tipsSummaryTitle": "Tips Summary",
    "Reports.tipsSummaryDesc": "Analysis of tip amounts received.",
    "Reports.tipsSummary.teamMember": "Team member",
    "Reports.tipsSummary.tipsCollected": "Tips collected",
    "Reports.tipsSummary.tipsRefunded": "Tips refunded",
    "Reports.tipsSummary.totalTips": "Total tips",
    "Reports.tipsSummary.total": "Total",
    "Reports.tipsSummary.noData": "No tips data found for this period.",
    "Reports.tipsDetails.saleNumber": "Sale #",
    "Reports.tipsDetails.saleDate": "Sale date",
    "Reports.tipsDetails.teamMember": "Team member",
    "Reports.tipsDetails.client": "Client",
    "Reports.tipsDetails.establishment": "Establishment",
    "Reports.tipsDetails.transactionType": "Transaction type",
    "Reports.tipsDetails.channel": "Channel",
    "Reports.tipsDetails.paymentMethod": "Payment method",
    "Reports.tipsDetails.tipsCollected": "Tips collected",
    "Reports.tipsDetails.total": "Total",
    "Reports.tipsDetails.sale": "Sale",
    "Reports.tipsDetails.atPointOfSale": "At point of sale",
    "Reports.tipsDetails.noData": "No tips detail data found for this period.",
    "Reports.tipsDetailsTitle": "Tips Details",
    "Reports.tipsDetailsDesc": "Complete breakdown of all tips received.",
    "Reports.commissionActivityTitle": "Commission Activity",
    "Reports.commissionActivityDesc": "Complete list of all sales with payable commissions.",
    "Reports.commissionSummaryTitle": "Commission Summary",
    "Reports.commissionSummaryDesc": "Overview of commissions earned by team members, locations, and items sold.",
    "Reports.commissionActivity.saleNumber": "Sale #",
    "Reports.commissionActivity.saleDate": "Sale date",
    "Reports.commissionActivity.client": "Client",
    "Reports.commissionActivity.type": "Type",
    "Reports.commissionActivity.typeService": "Service",
    "Reports.commissionActivity.typeProduct": "Product",
    "Reports.commissionActivity.article": "Article",
    "Reports.commissionActivity.teamMember": "Team member",
    "Reports.commissionActivity.grossSales": "Gross sales",
    "Reports.commissionActivity.taxes": "Taxes",
    "Reports.commissionActivity.discounts": "Discounts",
    "Reports.commissionActivity.cost": "Cost",
    "Reports.commissionActivity.commissionBase": "Commission base",
    "Reports.commissionActivity.commission": "Commission",
    "Reports.commissionActivity.commissionPercent": "Commission %",
    "Reports.commissionActivity.total": "Total",
    "Reports.commissionActivity.noData": "No commission activity data found for this period.",
    // Commission par paliers (tâche 9 du chantier commissions-paliers)
    "Reports.commissionActivity.tiered": "Tiered",
    "Reports.commissionActivity.atCumulative": "at cumulative",
    "Reports.commissionActivity.outsideCumulative": "outside cumulative, invoice not fully paid",
    "Reports.commissionActivity.tierAmountHidden": "Tiered amount not shown: your access does not cover all the pay data needed to calculate it for this team member.",
    "Reports.commissionActivity.tierAmountPending": "Tiered amount loading.",
    "Reports.commissionActivity.amountReserved": "Restricted",
    "Reports.commissionActivity.amountReservedHint": "Commission not shown: you can only view your own pay.",
    "Reports.commissionActivity.tierBlockTitle": "Tiered commission",
    "Reports.commissionActivity.tierBlockNote": "Calculated by the payroll engine on the paid sales of the selected period only, like the pay stub: a partially paid invoice is not counted. The pay stub remains the only reference for the amount paid.",
    "Reports.commissionActivity.tierCategoryService": "Services",
    "Reports.commissionActivity.tierCategoryProduct": "Products",
    "Reports.commissionActivity.tierColumnBracket": "Cumulative bracket",
    "Reports.commissionActivity.tierColumnBase": "Amount",
    "Reports.commissionActivity.tierColumnRate": "Rate",
    "Reports.commissionActivity.tierBelowThreshold": "Below threshold, 0 to {to}",
    "Reports.commissionActivity.tierBand": "Tier {n}, {from} to {to}",
    "Reports.commissionActivity.tierBandOpen": "Tier {n}, starting at {from}",
    "Reports.commissionActivity.tierCumulative": "Period cumulative",
    "Reports.commissionActivity.tierTotal": "Tiered commission for the period",
    "Reports.commissionSummary.tieredBadge": "Tiered",
    "Reports.commissionSummary.tierAmountHidden": "Tiered amount not shown: your access does not cover all the pay data needed to calculate it for this team member.",
    "Reports.commissionSummary.tierAmountPending": "Tiered amount loading.",
    "Reports.commissionSummary.amountReserved": "Restricted",
    "Reports.commissionSummary.amountReservedHint": "Commission not shown: you can only view your own pay.",
    "Reports.commissionSummary.tieredRateHint": "Indicative rate: it compares the commission calculated by the payroll engine with this report's commission base.",
    "Reports.commissionSummary.tieredBadgeHint": "This team member is paid on a tiered scale applied to the period cumulative. For the tiered categories, the commission shown comes from the payroll engine, on the paid sales of the selected period only. The pay stub remains the only reference for the amount paid.",
    "Reports.commissionSummary.teamMember": "Team member",
    "Reports.commissionSummary.qtySold": "Qty sold",
    "Reports.commissionSummary.itemsSold": "Items sold",
    "Reports.commissionSummary.grossSales": "Gross sales",
    "Reports.commissionSummary.refunds": "Refunds",
    "Reports.commissionSummary.taxes": "Taxes",
    "Reports.commissionSummary.discounts": "Discounts",
    "Reports.commissionSummary.costs": "Costs",
    "Reports.commissionSummary.commissionBase": "Commission base",
    "Reports.commissionSummary.commission": "Commission",
    "Reports.commissionSummary.commissionPercent": "Commission %",
    "Reports.commissionSummary.total": "Total",
    "Reports.commissionSummary.noData": "No commission summary data found for this period.",
    "Reports.teamProductivityTitle": "Team Productivity",
    "Reports.teamProductivityDesc": "Productivity metrics by team member: clients, services, products, and rebooking rate.",
    "Reports.teamProductivity.teamMember": "Team member",
    "Reports.teamProductivity.workingDays": "Working days",
    "Reports.teamProductivity.hoursWorked": "Hours worked",
    "Reports.teamProductivity.clientsPerDay": "Clients/day",
    "Reports.teamProductivity.clientsPerHour": "Clients/hour",
    "Reports.teamProductivity.servicesPerHour": "Services/hour",
    "Reports.teamProductivity.servicesPerClient": "Services/client",
    "Reports.teamProductivity.productsPerClient": "Products/client",
    "Reports.teamProductivity.prebookingRate": "Pre-booking",
    "Reports.teamProductivity.total": "Total",
    "Reports.teamProductivity.na": "N/A",
    "Reports.teamProductivity.noData": "No productivity data found for this period.",
    "Reports.teamProductivity.sourceLabel": "Days/hours source",
    "Reports.teamProductivity.sourceSchedule": "Planned schedule",
    "Reports.teamProductivity.sourceTimesheet": "Payroll timesheets",
    "Reports.teamProductivity.sourceHelp": "Sets where “Working days” and “Hours worked” come from. “Planned schedule” uses the configured opening hours with exceptional days applied and blocked time removed. Available to every plan. “Payroll timesheets” uses actual clocked entries (payroll module). With a shared salon schedule (no individual schedules), every employee shows the same planned hours.",
    "Reports.teamProductivity.sourceSaveFailed": "Could not save the source setting.",
    // Client reports
    "Reports.clientsSummaryTitle": "Clients Summary",
    "Reports.clientsSummaryDesc": "Overview of new and recurring clients with appointments and walk-in clients in the selected period.",
    "Reports.clientsSummary.noData": "No clients with appointments for this period",
    "Reports.clientsSummary.uniqueClients": "Unique clients",
    "Reports.clientsSummary.newClients": "New",
    "Reports.clientsSummary.returningClients": "Returning",
    "Reports.clientsSummary.retentionRate": "Retention rate",
    "Reports.clientsSummary.byEmployee": "By employee",
    "Reports.clientsSummary.byPeriod": "Trend",
    "Reports.clientsSummary.topClients": "Top clients",
    "Reports.clientsSummary.column.employee": "Employee",
    "Reports.clientsSummary.column.uniqueClients": "Unique clients",
    "Reports.clientsSummary.column.newClients": "New",
    "Reports.clientsSummary.column.returningClients": "Returning",
    "Reports.clientsSummary.column.period": "Period",
    "Reports.clientsSummary.column.rank": "Rank",
    "Reports.clientsSummary.column.customer": "Customer",
    "Reports.clientsSummary.column.appointmentCount": "# appointments",
    "Reports.clientsSummary.column.revenue": "Revenue",
    "Reports.clientRetentionTitle": "Client Retention by Employee",
    "Reports.clientRetentionDesc": "Compares the selected period with the previous one: for each employee, how many of their clients came back, with whom, and how many were lost.",
    "Reports.clientRetention.noData": "No employees found for this period",
    "Reports.clientRetention.filter.currentPeriod": "Current period",
    "Reports.clientRetention.filter.previousPeriod": "Previous period",
    "Reports.clientRetention.group.previousPeriod": "Previous period",
    "Reports.clientRetention.group.returnedSalonOrEmployee": "Clients returned with salon and/or employee",
    "Reports.clientRetention.group.returnedWithEmployee": "Clients returned with the employee",
    "Reports.clientRetention.group.returnedSalonOnly": "Clients returned with salon",
    "Reports.clientRetention.column.employee": "Employee",
    "Reports.clientRetention.column.active": "Active",
    "Reports.clientRetention.column.totalClients": "Total clients",
    "Reports.clientRetention.column.pctClients": "% Clients",
    "Reports.clientRetention.column.newClients": "New clients",
    "Reports.clientRetention.column.pctNewClients": "% New clients",
    "Reports.clientRetention.column.existingClients": "Existing clients",
    "Reports.clientRetention.column.pctExistingClients": "% Existing clients",
    "Reports.clientRetention.column.newPlusExisting": "New + existing",
    "Reports.clientRetention.column.pctNewPlusExisting": "% New + existing",
    "Reports.clientRetention.column.lostClients": "Lost clients",
    "Reports.clientRetention.column.pctLostClients": "% Lost clients",
    "Reports.clientRetention.column.returnedClients": "Returned clients",
    "Reports.clientRetention.column.pctReturnedClients": "% Returned clients",
    "Reports.clientsListTitle": "Clients List",
    "Reports.clientsListDesc": "Complete list of all active clients.",
    "Reports.clientsList.client": "Client",
    "Reports.clientsList.phone": "Phone",
    "Reports.clientsList.email": "Email",
    "Reports.clientsList.addedOn": "Added on",
    "Reports.clientsList.firstAppointment": "First appointment",
    "Reports.clientsList.lastAppointment": "Last appointment",
    "Reports.clientsList.clientType": "Client type",
    "Reports.clientsList.total": "Total",
    "Reports.clientsList.noData": "No clients visited during this period.",
    "Reports.clientsList.clientTypeNew": "New",
    "Reports.clientsList.clientTypeReturning": "Returning",
    "Reports.clientsList.clientTypeLoyal": "Loyal",
    "Reports.clientsList.clientTypeFlagged": "Flagged",
    "Reports.clientsList.showing": "Showing",
    "Reports.clientsList.of": "of",
    "Reports.clientsList.results": "results",
    "Reports.byGenderTitle": "Clientele breakdown by sex",
    "Reports.byGenderDesc": "How your active clientele breaks down by declared sex.",
    "Reports.byLanguageTitle": "Clientele's preferred languages",
    "Reports.byLanguageDesc": "Your active clients' preferred communication languages.",
    "Reports.bySourceTitle": "Clientele acquisition channels",
    "Reports.bySourceDesc": "Which channels your active clients found you through.",
    "Reports.byEmployeeTitle": "Clientele's employee preferences",
    "Reports.byEmployeeDesc": "Which team members your active clients prefer.",
    "Reports.notifFailuresTitle": "Notification failures",
    "Reports.notifFailuresDesc": "Clients who were not reached (SMS undelivered or landline)",
    "Reports.notifFailures.date": "Date",
    "Reports.notifFailures.client": "Client",
    "Reports.notifFailures.channel": "Channel",
    "Reports.notifFailures.reason": "Reason",
    "Reports.notifFailures.channelValue.sms": "SMS",
    "Reports.notifFailures.channelValue.email": "Email",
    "Reports.notifFailures.reasonValue.undelivered": "Undelivered",
    "Reports.notifFailures.reasonValue.failed": "Send failed",
    "Reports.notifFailures.reasonValue.landline_no_sms": "Landline: no SMS",
    "Reports.notifFailures.total": "Total",
    "Reports.notifFailures.empty": "No notification failures for this period",
    "Reports.breakdown.category": "Category",
    "Reports.breakdown.count": "# Clients",
    "Reports.breakdown.pct": "%",
    "Reports.breakdown.avgBasket": "Avg. basket",
    "Reports.breakdown.avgVisits": "Avg. visits",
    "Reports.breakdown.total": "Total",
    "Reports.breakdown.notProvided": "Not provided",
    "Reports.breakdown.noPreferredEmployee": "No preferred employee",
    "Reports.genderValue.homme": "Male",
    "Reports.genderValue.femme": "Female",
    "Reports.genderValue.autre": "Other",
    "Reports.genderValue.prefere_ne_pas_dire": "Prefer not to say",
    "Reports.languageValue.fr": "French",
    "Reports.languageValue.en": "English",
    "Reports.sourceValue.google": "Google",
    "Reports.sourceValue.social": "Social media",
    "Reports.sourceValue.referral": "Referral",
    "Reports.sourceValue.walkin": "Walk-in",
    "Reports.sourceValue.online_booking": "Online booking",
    "Reports.sourceValue.competitor": "Former client (competitor)",
    "Reports.sourceValue.other": "Other",
    "Reports.clientDataTitle": "Client Data",
    "Reports.clientDataDesc": "Study each client's behavior and preferences in detail.",
    "Reports.clientData.client": "Client",
    "Reports.clientData.phone": "Phone",
    "Reports.clientData.email": "Email",
    "Reports.clientData.addedOn": "Added on",
    "Reports.clientData.firstAppointment": "First appt.",
    "Reports.clientData.lastAppointment": "Last appt.",
    "Reports.clientData.totalAppointments": "Total appts.",
    "Reports.clientData.totalSpent": "Total amount",
    "Reports.clientData.clientType": "Client type",
    "Reports.clientData.total": "Total",
    "Reports.clientData.clients": "clients",
    "Reports.clientData.noData": "No client data for this period.",
    "Reports.clientData.showing": "Showing",
    "Reports.clientData.of": "of",
    "Reports.clientData.results": "results",
    // Inventory reports
    "Reports.availableStockTitle": "Available Stock",
    "Reports.availableStockDesc": "Current status and quantity of items in stock.",
    "Reports.availableStock.brand": "Brand",
    "Reports.availableStock.sku": "Main SKU",
    "Reports.availableStock.product": "Product",
    "Reports.availableStock.stock": "Available stock",
    "Reports.availableStock.totalCost": "Total cost",
    "Reports.availableStock.avgCost": "Avg. cost",
    "Reports.availableStock.retailPrice": "Retail price",
    "Reports.availableStock.retailAmount": "Retail sales amount",
    "Reports.availableStock.margin": "Margin %",
    "Reports.availableStock.lowThreshold": "Low stock threshold",
    "Reports.availableStock.total": "Total",
    "Reports.availableStock.noData": "No products found.",
    "Reports.availableStock.live": "Live",
    "Reports.availableStock.asOfDay": "Stock on {date} at 8:00 AM",
    "Reports.availableStock.currentPricesNote": "Current costs and retail prices",
    "Reports.availableStock.dayAt8": "{date}, 8:00 AM",
    "Reports.stockMovementsSummaryTitle": "Stock Movements Summary",
    "Reports.stockMovementsSummaryDesc": "Summary of stock inflows and outflows.",
    "Reports.stockMovements.brand": "Brand",
    "Reports.stockMovements.sku": "Main SKU",
    "Reports.stockMovements.product": "Product",
    "Reports.stockMovements.initialStock": "Initial stock",
    "Reports.stockMovements.received": "Received",
    "Reports.stockMovements.sold": "Sold",
    "Reports.stockMovements.adjusted": "Adjusted",
    "Reports.stockMovements.internalUse": "Internal use",
    "Reports.stockMovements.losses": "Losses",
    "Reports.stockMovements.returns": "Returns",
    "Reports.stockMovements.finalStock": "Final stock",
    "Reports.stockMovements.total": "Total",
    "Reports.stockMovements.noData": "No stock movements for this period.",
    "Reports.stockMovementsJournalTitle": "Stock Movements Journal",
    "Reports.stockMovementsJournalDesc": "Detailed journal of all stock movements.",
    "Reports.internalUseLossTitle": "Internal use and losses",
    "Reports.internalUseLossDesc": "Product consumed by the salon or lost, valued at cost",
    "Reports.internalUseLoss.brand": "Brand",
    "Reports.internalUseLoss.sku": "SKU",
    "Reports.internalUseLoss.product": "Product",
    "Reports.internalUseLoss.internalQty": "Internal use (qty)",
    "Reports.internalUseLoss.internalValue": "Internal use value",
    "Reports.internalUseLoss.lossQty": "Losses (qty)",
    "Reports.internalUseLoss.lossValue": "Loss value",
    "Reports.internalUseLoss.totalQty": "Total out",
    "Reports.internalUseLoss.totalValue": "Total value",
    "Reports.internalUseLoss.total": "Total",
    "Reports.internalUseLoss.noData": "No internal use or loss over this period",
    "Reports.stockJournal.date": "Date",
    "Reports.stockJournal.brand": "Brand",
    "Reports.stockJournal.sku": "SKU",
    "Reports.stockJournal.product": "Product",
    "Reports.stockJournal.movementType": "Adjustment reason",
    "Reports.stockJournal.reference": "Adjustment ref.",
    "Reports.stockJournal.quantity": "Qty",
    "Reports.stockJournal.cost": "Cost",
    "Reports.stockJournal.avgCost": "Avg. cost",
    "Reports.stockJournal.total": "Total",
    "Reports.stockJournal.noData": "No stock movements for this period.",
    "Reports.stockJournal.type.transfer": "Transfer",
    "Reports.productsListTitle": "Products List",
    "Reports.productsListDesc": "Complete list of all products.",
    "Reports.productsList.sku": "Main SKU",
    "Reports.productsList.product": "Product",
    "Reports.productsList.category": "Category",
    "Reports.productsList.barcode": "Barcode",
    "Reports.productsList.supplier": "Supplier",
    "Reports.productsList.description": "Description",
    "Reports.productsList.image": "Image",
    "Reports.productsList.units": "Units",
    "Reports.productsList.uom": "UoM",
    "Reports.productsList.retailPrice": "Retail price",
    "Reports.productsList.total": "Total",
    "Reports.productsList.noData": "No products found.",
    "Reports.workHours.teamMember": "Team member",
    "Reports.workHours.date": "Date",
    "Reports.workHours.source": "Source",
    "Reports.workHours.scheduledStart": "Scheduled start",
    "Reports.workHours.actualStart": "Actual start",
    "Reports.workHours.startVariance": "Start variance",
    "Reports.workHours.scheduledEnd": "Scheduled end",
    "Reports.workHours.actualEnd": "Actual end",
    "Reports.workHours.endVariance": "End variance",
    "Reports.workHours.scheduled": "Scheduled",
    "Reports.workHours.worked": "Worked",
    "Reports.workHours.unpaidBreaks": "Unpaid breaks",
    "Reports.workHours.totalPaid": "Total paid hours",
    "Reports.workHours.total": "Total",
    "Reports.workHours.noData": "No timesheet entries for this period.",
    "Reports.workHours.manual": "Manual",
    "Reports.workHours.punch": "Punch",
    "Reports.breakActivity.teamMember": "Team member",
    "Reports.breakActivity.date": "Date",
    "Reports.breakActivity.source": "Source",
    "Reports.breakActivity.type": "Type",
    "Reports.breakActivity.revenue": "Revenue",
    "Reports.breakActivity.scheduledDuration": "Scheduled duration",
    "Reports.breakActivity.actualDuration": "Actual duration",
    "Reports.breakActivity.total": "Total",
    "Reports.breakActivity.noData": "No break activity for this period.",
    "Reports.breakActivity.meal": "Meal",
    "Reports.breakActivity.break": "Break",
    "Reports.breakActivity.unpaid": "Unpaid",
    "Reports.breakActivity.manual": "Manual",
    "Reports.breakActivity.punch": "Punch",
    "Reports.attendance.teamMember": "Team member",
    "Reports.attendance.scheduledShifts": "Scheduled shifts",
    "Reports.attendance.onTimeStarts": "On-time starts",
    "Reports.attendance.earlyStarts": "Early starts",
    "Reports.attendance.lateStarts": "Late starts",
    "Reports.attendance.onTimeEnds": "On-time ends",
    "Reports.attendance.earlyEnds": "Early ends",
    "Reports.attendance.lateEnds": "Late ends",
    "Reports.attendance.punctuality": "Punctuality",
    "Reports.attendance.missedShifts": "Missed shifts",
    "Reports.attendance.attendanceRate": "Attendance",
    "Reports.attendance.total": "Total",
    "Reports.attendance.noData": "No attendance data for this period.",
    "Reports.salaryDetails.day": "Day",
    "Reports.salaryDetails.teamMember": "Team member",
    "Reports.salaryDetails.workedHours": "Worked hours",
    "Reports.salaryDetails.unpaidBreaks": "Unpaid breaks",
    "Reports.salaryDetails.regularPaidHours": "Regular paid hours",
    "Reports.salaryDetails.overtimePaidHours": "Overtime paid hours",
    "Reports.salaryDetails.totalPaidHours": "Total paid hours",
    "Reports.salaryDetails.hourlyRate": "Hourly rate",
    "Reports.salaryDetails.overtimeRate": "Overtime rate",
    "Reports.salaryDetails.totalSalary": "Total salary",
    "Reports.salaryDetails.total": "Total",
    "Reports.salaryDetails.noData": "No salary data for this period.",
    "Reports.salarySummary.teamMember": "Team member",
    "Reports.salarySummary.workedHours": "Worked hours",
    "Reports.salarySummary.unpaidBreaks": "Unpaid breaks",
    "Reports.salarySummary.regularPaidHours": "Regular paid hours",
    "Reports.salarySummary.overtimePaidHours": "Overtime paid hours",
    "Reports.salarySummary.totalPaidHours": "Total paid hours",
    "Reports.salarySummary.hourlyRate": "Hourly rate",
    "Reports.salarySummary.overtimeRate": "Overtime rate",
    "Reports.salarySummary.totalSalary": "Total salary",
    "Reports.salarySummary.total": "Total",
    "Reports.salarySummary.noData": "No salary data for this period.",
    "Reports.feeDeductions.teamMember": "Team member",
    "Reports.feeDeductions.deductionName": "Deduction name",
    "Reports.feeDeductions.type": "Type",
    "Reports.feeDeductions.frequency": "Frequency",
    "Reports.feeDeductions.amount": "Amount",
    "Reports.feeDeductions.effectiveDate": "Effective date",
    "Reports.feeDeductions.endDate": "End date",
    "Reports.feeDeductions.status": "Status",
    "Reports.feeDeductions.total": "Total",
    "Reports.feeDeductions.noData": "No deductions found.",
    "Reports.feeDeductions.recurring": "Recurring",
    "Reports.feeDeductions.oneTime": "One-time",
    "Reports.feeDeductions.daily": "Daily",
    "Reports.feeDeductions.revenuePercent": "Revenue %",
    "Reports.feeDeductions.perPay": "Per pay",
    "Reports.feeDeductions.weekly": "Weekly",
    "Reports.feeDeductions.biweekly": "Biweekly",
    "Reports.feeDeductions.monthly": "Monthly",
    "Reports.feeDeductions.perDay": "Per day",
    "Reports.feeDeductions.perPeriod": "Per period",
    "Reports.feeDeductions.active": "Active",
    "Reports.feeDeductions.inactive": "Inactive",
    "Reports.feeDeductions.dailyDeduction": "Daily deduction",
    "Reports.feeDeductions.revenueDeduction": "Revenue % deduction",
    "Reports.stockToOrderTitle": "Stock to order",
    "Reports.stockToOrderDesc": "Products at or below their low stock threshold and the quantity to order to get back to the ideal quantity.",
    "Reports.stockToOrder.supplier": "Supplier",
    "Reports.stockToOrder.product": "Product",
    "Reports.stockToOrder.sku": "SKU",
    "Reports.stockToOrder.stock": "Stock",
    "Reports.stockToOrder.threshold": "Threshold",
    "Reports.stockToOrder.ideal": "Ideal",
    "Reports.stockToOrder.sold": "Sold (period)",
    "Reports.stockToOrder.toOrder": "To order",
    "Reports.stockToOrder.unitCost": "Unit cost",
    "Reports.stockToOrder.estimatedCost": "Estimated cost",
    "Reports.stockToOrder.idealToDefine": "Ideal to define",
    "Reports.stockToOrder.noLongerToOrder": "No longer to order",
    "Reports.stockToOrder.noSupplier": "No supplier",
    "Reports.stockToOrder.products": "{count} products",
    "Reports.stockToOrder.productsOne": "{count} product",
    "Reports.stockToOrder.units": "{count} units",
    "Reports.stockToOrder.unitsOne": "{count} unit",
    "Reports.stockToOrder.partialCost": "partial cost, {count} products without cost price",
    "Reports.stockToOrder.partialCostOne": "partial cost, {count} product without cost price",
    "Reports.stockToOrder.total": "TOTAL",
    "Reports.stockToOrder.noData": "No products to order.",
    "Reports.stockToOrder.saved": "Saved",
    "Reports.stockToOrder.saveFailed": "The value could not be saved.",
    "Reports.inventoryPreviewTitle": "Inventory Report Preview",
    "Reports.inventoryPreviewDesc": "The report will include all stock movements (Purchases, Sales, Returns, Waste and Adjustments) for the selected period, with a summary table and a detailed chronological list.",
    "Reports.downloadInventoryReport": "Download inventory report",
    "Reports.period": "Period:",
    "Reports.inventory.totalMovements": "Total movements",
    "Reports.inventory.valueIn": "Value in",
    "Reports.inventory.valueOut": "Value out",
    "Reports.inventory.movementSummary": "Movement summary",
    "Reports.inventory.productBreakdown": "Products affected",
    "Reports.inventory.purchase": "Purchase",
    "Reports.inventory.sale": "Sale",
    "Reports.inventory.return": "Return to supplier",
    "Reports.inventory.waste": "Waste",
    "Reports.inventory.internal_use": "Used by the salon",
    "Reports.inventory.adjustment": "Adjustment",
    "Reports.inventory.netChange": "Net change",
    "Reports.inventory.product": "Product",
    "Reports.inventory.sku": "SKU",
    "Reports.inventory.initialStock": "Initial stock",
    "Reports.inventory.finalStock": "Final stock",
    "Reports.inventory.variation": "Variation",
    "Reports.inventory.movements": "Movements",
    "Reports.inventory.noMovements": "No inventory movements for the selected period.",
    "Reports.inventory.type": "Type",
    "Reports.inventory.quantity": "Quantity",
    "Reports.inventory.selectProducts": "Select products",
    "Reports.inventory.selectAll": "Select all",
    "Reports.inventory.deselectAll": "Deselect all",
    "Reports.inventory.searchProducts": "Search products...",
    "Reports.noAccessTitle": "No access",
    "Reports.noAccessDescription": "You do not have permission to view reports. Contact the salon owner to request access.",
    "Reports.noAllowedReports": "No report is open to you. Please contact the salon owner.",
    "Reports.snapshotAt": "Snapshot at",
    "Reports.print": "Print",

    // Reports — shared overview section (headline figures, as a table)
    "Reports.overview.title": "Overview",
    "Reports.overview.measure": "Measure",
    "Reports.overview.value": "Value",
    "Reports.serviceVsRetailTitle": "Service vs Retail",
    "Reports.serviceVsRetailDesc": "Compare service and retail on the same tickets, and see who sells product",
    "Reports.serviceVsRetail.noData": "No sales over this period",
    "Reports.serviceVsRetail.netHt": "Net sales",
    "Reports.serviceVsRetail.units": "Units sold",
    "Reports.serviceVsRetail.tickets": "Tickets",
    "Reports.serviceVsRetail.avgPerOwnTicket": "Average per ticket",
    "Reports.serviceVsRetail.avgPerAllTickets": "Average, all tickets",
    "Reports.serviceVsRetail.unitsPerOwnTicket": "Units per ticket",
    "Reports.serviceVsRetail.unitsPerClient": "Units per client",
    "Reports.serviceVsRetail.avgPerWeek": "Average per week",
    "Reports.serviceVsRetail.column.measure": "Measure",
    "Reports.serviceVsRetail.column.basis": "Basis",
    "Reports.serviceVsRetail.column.service": "Service",
    "Reports.serviceVsRetail.column.retail": "Retail",
    "Reports.serviceVsRetail.column.other": "Other",
    "Reports.serviceVsRetail.basis.ownTickets": "÷ its own tickets",
    "Reports.serviceVsRetail.basis.allTickets": "÷ {count} tickets",
    "Reports.serviceVsRetail.basis.allClients": "÷ {count} unique clients",
    "Reports.serviceVsRetail.basis.weeks": "÷ {count} weeks",
    "Reports.serviceVsRetail.basis.none": "—",
    "Reports.serviceVsRetail.sideBySide": "The three columns, side by side",
    "Reports.serviceVsRetail.crossRatios": "Cross ratios",
    "Reports.serviceVsRetail.ticketSplit": "Ticket breakdown",
    "Reports.serviceVsRetail.clientSplit": "Unique clients by purchase type",
    "Reports.serviceVsRetail.byEmployee": "By employee: who sells product?",
    "Reports.serviceVsRetail.ratio.avgTicketAll": "Average ticket (service and retail)",
    "Reports.serviceVsRetail.ratio.retailPerServiceTicket": "Retail per service ticket",
    "Reports.serviceVsRetail.ratio.retailPerServiceClient": "Retail per service client",
    "Reports.serviceVsRetail.ratio.retailUnitsPerServiceTicket": "Retail units per service ticket",
    "Reports.serviceVsRetail.ratio.retailUnitsPerServiceClient": "Retail units per service client",
    "Reports.serviceVsRetail.ratio.pctClientsBuyingRetail": "% of clients buying retail",
    "Reports.serviceVsRetail.ratio.pctRetailOverService": "% retail vs service",
    "Reports.serviceVsRetail.ratio.pctRetailOverTotal": "% retail vs total sales",
    "Reports.serviceVsRetail.split.total": "Total",
    "Reports.serviceVsRetail.split.serviceOnly": "Service only",
    "Reports.serviceVsRetail.split.retailOnly": "Retail only",
    "Reports.serviceVsRetail.split.both": "Service and retail",
    "Reports.serviceVsRetail.split.otherOnly": "Other only",
    "Reports.serviceVsRetail.split.count": "Count",
    "Reports.serviceVsRetail.split.clients": "Clients",
    "Reports.serviceVsRetail.split.percent": "%",
    "Reports.serviceVsRetail.employee.name": "Employee",
    "Reports.serviceVsRetail.employee.serviceNetHt": "Service $",
    "Reports.serviceVsRetail.employee.retailNetHt": "Retail $",
    "Reports.serviceVsRetail.employee.pctRetailOverService": "Retail / service",
    "Reports.serviceVsRetail.employee.retailPerServiceTicket": "Retail / service ticket",
    "Reports.serviceVsRetail.employee.pctClientsBuyingRetail": "% retail clients",
    "Reports.serviceVsRetail.note.amountsExclTax": "Amounts exclude tax.",
    "Reports.serviceVsRetail.note.noCustomer": "Invoices with no client attached are excluded from the per-client rows.",

    // Reports — unified header
    "Reports.tileAvailable": "Available reports",
    "Reports.tileCategories": "Categories",
    "Reports.tileInDevelopment": "In development",
    "Reports.tileEnterprise": "Enterprise",
    "Reports.enterpriseBadge": "Enterprise",
    "Reports.variablePriceExcluded": "{count} appointment(s) at a variable price, not counted",
  },
  fr: {
    "Reports.depositsHeldTitle": "Acomptes détenus",
    "Reports.depositsHeldDesc": "Acomptes encaissés en ligne et pas encore appliqués à une facture.",
    "Reports.depositsHeld.held": "Actuellement détenus",
    "Reports.depositsHeld.heldHint": "Dus aux clients tant que la prestation n'est pas rendue.",
    "Reports.depositsHeld.heldAllTime": "Sur toute la période d'activité, quelle que soit la période affichée ci-dessus.",
    "Reports.depositsHeld.applied": "Appliqués à des factures",
    "Reports.depositsHeld.refunded": "Remboursés",
    "Reports.depositsHeld.collected": "Encaissés sur la période",
    "Reports.depositsHeld.liabilityNote": "Un acompte est de l'argent détenu pour une prestation non encore rendue, pas du chiffre d'affaires. Le revenu reste reconnu sur la facture finale, à laquelle l'acompte s'applique.",
    "Reports.customerBalancesTitle": "Soldes clients",
    "Reports.customerBalancesDesc": "Montants dus par les clients, et règlements de la période",
    "Reports.customerBalances.notRevenue": "Ce n'est pas du chiffre d'affaires. Une facture partiellement payée a déjà compté sa vente entière le jour de son émission.",
    "Reports.customerBalances.outstanding": "Montants dus au jour d'aujourd'hui",
    "Reports.customerBalances.outstandingHint": "Ce montant est une photo prise aujourd'hui. Il n'est pas affecté par la période sélectionnée ci-dessus.",
    "Reports.customerBalances.periodMoves": "Mouvements de la période",
    "Reports.customerBalances.total": "Total",
    "Reports.customerBalances.customer": "Client",
    "Reports.customerBalances.amountOwed": "Montant dû",
    "Reports.customerBalances.since": "Depuis",
    "Reports.customerBalances.origin": "Origine",
    "Reports.customerBalances.sourceInvoice": "Facture",
    "Reports.customerBalances.sourceManual": "Manuel",
    "Reports.customerBalances.sourceMixed": "Mixte",
    "Reports.customerBalances.noOutstanding": "Aucun client ne doit rien au salon actuellement.",
    "Reports.customerBalances.created": "Dus créés",
    "Reports.customerBalances.settled": "Dus réglés",
    "Reports.customerBalances.noMovements": "Aucun mouvement sur cette période.",
    "Reports.customerBalances.date": "Date",
    "Reports.customerBalances.reason": "Motif",
    "Reports.customerBalances.method": "Méthode",
    "Reports.customerBalances.by": "Par",
    "Reports.customerBalances.section": "Section",
    "Reports.customerBalances.reasonOrMethod": "Motif / méthode",
    "Reports.customerBalances.unknownCustomer": "Client non identifié",
    "Reports.customerBalances.splitCustomer": "Facture répartie entre plusieurs clients",
    "Reports.customerBalances.creditsOutstanding": "Avoirs en circulation",
    "Reports.customerBalances.creditsOutstandingHint": "Les avoirs encore dépensables au dossier des clients aujourd'hui. Ce montant est une photo prise aujourd'hui : il n'est pas affecté par la période sélectionnée ci-dessus, et il ne se soustrait jamais des montants dus. Il ne couvre que les avoirs, ce n'est donc pas tout ce que le salon doit à ses clients : les cartes cadeaux sont aussi de l'argent dû, et aucun rapport ne les lit encore.",
    "Reports.customerBalances.noCreditsOutstanding": "Aucun client ne porte d'avoir dépensable actuellement.",
    "Reports.customerBalances.creditsGranted": "Avoirs accordés sur la période",
    "Reports.customerBalances.creditsSaleReversal": "Reprises de vente",
    "Reports.customerBalances.creditsSaleReversalHint": "Une vente rendue, gardée au dossier du client au lieu d'être remboursée en argent.",
    "Reports.customerBalances.creditsGoodwill": "Gestes commerciaux",
    "Reports.customerBalances.creditsGoodwillHint": "De l'argent offert, sans aucune vente reprise. C'est une charge commerciale, et c'est ici que se lit ce que votre équipe a offert.",
    "Reports.customerBalances.creditAmount": "Montant du crédit",
    "Reports.customerBalances.creditNature": "Nature",
    "Reports.customerBalances.creditNumber": "Avoir",
    "Reports.customerBalances.creditCancelled": "Annulé",
    "Reports.customerBalances.natureSaleReversal": "Reprise de vente",
    "Reports.customerBalances.natureGoodwill": "Geste commercial",
    "Reports.customerBalances.natureMixed": "Mixte",
    "Reports.customerBalances.creditsHidden": "Les avoirs ne s'affichent qu'avec l'accès au module Facturation.",
    "Reports.ownScope.emptyTitle": "Aucune donnée pour votre compte",
    "Reports.ownScope.emptyDesc": "Votre compte est limité à vos propres rapports, mais aucune fiche prestataire n'y est liée. Contactez le propriétaire du salon.",

    // Reports - Selection Dialog
    "Reports.selection.title": "Sélectionner un rapport",
    "Reports.selection.period": "Période: ",
    "Reports.selection.invoice": "facture",
    "Reports.selection.invoices": "factures",
    "Reports.selection.generateReport": "Générer le rapport",
    "Reports.selection.financialTitle": "Rapport financier",
    "Reports.selection.financialQuestion": "Comment va mon entreprise financièrement?",
    "Reports.selection.salesTitle": "Rapport des ventes",
    "Reports.selection.salesQuestion": "Qu'est-ce qui se vend et quel est mon mix produit/service?",
    "Reports.selection.clientTitle": "Rapport clientèle",
    "Reports.selection.clientQuestion": "Qui sont mes clients et sont-ils fidèles?",
    "Reports.selection.employeeTitle": "Rapport employés",
    "Reports.selection.employeeQuestion": "Comment performent mes employés?",

    // Reports - Common
    "Reports.common.period": "Période: ",
    "Reports.common.invoice": "facture",
    "Reports.common.invoices": "factures",
    "Reports.common.executiveSummary": "Résumé exécutif",
    "Reports.common.generating": "Génération en cours...",
    "Reports.common.category": "Catégorie",
    "Reports.common.amount": "Montant",
    "Reports.common.type": "Type",
    "Reports.common.value": "Valeur",
    "Reports.common.service": "Service",
    "Reports.common.product": "Produit",

    // Reports - Financial
    "Reports.financial.title": "Rapport financier",
    "Reports.financial.totalCollected": "Total encaissé",
    "Reports.financial.totalCollectedSub": "Montant total collecté",
    "Reports.financial.netRevenue": "Revenu net",
    "Reports.financial.netRevenueSub": "Après rabais, avant taxes",
    "Reports.financial.averageTicket": "Ticket moyen",
    "Reports.financial.detailedSummary": "Résumé financier détaillé",
    "Reports.financial.percentOfTotal": "% du total encaissé",
    "Reports.financial.grossTotal": "Total brut",
    "Reports.financial.discounts": "Rabais",
    "Reports.financial.subtotal": "Sous-total",
    "Reports.financial.tps": "TPS",
    "Reports.financial.tvq": "TVQ",
    "Reports.financial.tips": "Pourboires",
    "Reports.financial.netSalon": "Net salon (hors pourboires)",
    "Reports.financial.totalCollectedRow": "Total encaissé",
    "Reports.financial.includeInvoices": "Inclure les factures détaillées (Table B)",
    "Reports.financial.includeInvoicesDesc": "Ajoute une liste complète de toutes les factures avec détails par ligne",
    "Reports.financial.download": "Télécharger le rapport financier",
    "Reports.financial.paymentBreakdown": "Répartition par mode de paiement",
    "Reports.financial.paymentMethod": "Mode de paiement",
    "Reports.financial.creditCards": "Cartes de crédit",
    "Reports.financial.giftCards": "Cartes-cadeaux",
    "Reports.financial.cash": "Comptant",
    "Reports.financial.debitCard": "Carte de débit",
    "Reports.financial.eTransfer": "Virement électronique",
    "Reports.financial.check": "Chèque",
    "Reports.financial.other": "Autre",
    "Reports.financial.transactions": "transactions",
    "Reports.financial.count": "Nombre",
    "Reports.financial.visa": "Visa",
    "Reports.financial.mastercard": "Mastercard",
    "Reports.financial.amex": "American Express",
    "Reports.financial.creditCardSubtotal": "Sous-total cartes de crédit",

    // Reports - Sales
    "Reports.sales.title": "Rapport des ventes",
    "Reports.sales.serviceRevenue": "Revenus services",
    "Reports.sales.productRevenue": "Revenus produits",
    "Reports.sales.salesCount": "ventes",
    "Reports.sales.serviceMix": "Mix services/produits",
    "Reports.sales.salesBreakdown": "Répartition des ventes",
    "Reports.sales.salesBreakdownSub": "Répartition des ventes",
    "Reports.sales.revenue": "CA",
    "Reports.sales.percentRevenue": "% du CA",
    "Reports.sales.salesCountHeader": "Nombre de ventes",
    "Reports.sales.avgTicket": "Ticket moyen",
    "Reports.sales.services": "Services",
    "Reports.sales.products": "Produits",
    "Reports.sales.other": "Autres",
    "Reports.sales.topServices": "Services les plus performants",
    "Reports.sales.totalRevenue": "CA total",
    "Reports.sales.salesHeader": "# ventes",
    "Reports.sales.avgPrice": "Prix moyen",
    "Reports.sales.topProducts": "Produits les plus vendus",
    "Reports.sales.quantitySold": "Quantité vendue",
    "Reports.sales.revenueHeader": "Revenu",
    "Reports.sales.percentProductSales": "% ventes produits",
    "Reports.sales.crossSell": "Analyse Cross-sell",
    "Reports.sales.indicator": "Indicateur",
    "Reports.sales.serviceAndProduct": "Factures service + produit",
    "Reports.sales.serviceOnly": "Factures service seulement",
    "Reports.sales.withProduct": "Factures avec produit",
    "Reports.sales.attachmentRate": "Taux d'attachement produit",
    "Reports.sales.download": "Télécharger le rapport des ventes",

    // Reports - Client
    "Reports.client.title": "Rapport clientèle",
    "Reports.client.uniqueClients": "Clients uniques",
    "Reports.client.overPeriod": "sur la période",
    "Reports.client.recurringClients": "Clients récurrents",
    "Reports.client.ofClients": "des clients",
    "Reports.client.avgBasket": "Panier moyen/client",
    "Reports.client.avgSpend": "Dépense moyenne",
    "Reports.client.distribution": "Répartition clientèle",
    "Reports.client.clientType": "Type de client",
    "Reports.client.count": "Nombre",
    "Reports.client.newClients": "Nouveaux clients",
    "Reports.client.newClientsSub": "Première facture dans la période",
    "Reports.client.recurringClientsSub": ">1 facture dans la période",
    "Reports.client.oneTimeClients": "Clients d'une seule visite",
    "Reports.client.oneTimeClientsSub": "1 seule facture dans la période",
    "Reports.client.topClients": "Top clients (valeur réelle)",
    "Reports.client.client": "Client",
    "Reports.client.invoiceCount": "# Factures",
    "Reports.client.totalSpent": "Dépense totale",
    "Reports.client.avgBasketHeader": "Panier moyen",
    "Reports.client.recurring": "récurrent",
    "Reports.client.visitFrequency": "Fréquence de visite",
    "Reports.client.visits": "Nb visites",
    "Reports.client.lastVisit": "Dernière visite",
    "Reports.client.days": "jours",
    "Reports.client.atRisk": "À risque",
    "Reports.client.atRiskNote": "Les clients à risque n'ont pas visité le salon depuis plus de 60 jours",
    "Reports.client.download": "Télécharger le rapport clientèle",

    // Reports - Employee
    "Reports.employee.title": "Rapport employés",
    "Reports.employee.teamRevenue": "CA total équipe",
    "Reports.employee.teamRevenueSub": "Toutes ventes combinées",
    "Reports.employee.teamAvgTicket": "Ticket moyen équipe",
    "Reports.employee.topPerformer": "Meilleur employé",
    "Reports.employee.sectionCommission": "Section A : Rapport de COMMISSION",
    "Reports.employee.paySummary": "Sommaire de paye par employé",
    "Reports.employee.employee": "Employé",
    "Reports.employee.totalSales": "Total ventes",
    "Reports.employee.commissionPercent": "% Commission",
    "Reports.employee.commissionAmount": "Commission $",
    "Reports.employee.tips": "Pourboires",
    "Reports.employee.totalToPay": "Total à payer",
    "Reports.employee.noCommission": "Aucune commission",
    "Reports.employee.salesDetail": "Détail des ventes (justification de la paye)",
    "Reports.employee.sale": "vente",
    "Reports.employee.sales": "ventes",
    "Reports.employee.date": "Date",
    "Reports.employee.serviceProduct": "Service / Produit",
    "Reports.employee.commission": "Commission",
    "Reports.employee.commissionRequired": "Section A : Rapport de commission (configuration requise)",
    "Reports.employee.noCommissionRates": "Aucun taux de commission configuré pour les employés.",
    "Reports.employee.addCommissionRates": "Veuillez ajouter les taux de commission dans les paramètres des employés.",
    "Reports.employee.sectionPerformance": "Section B : Rapport PERFORMANCE (pour les revues mensuelles)",
    "Reports.employee.globalPerformance": "Performance globale employé",
    "Reports.employee.revenueGenerated": "CA généré",
    "Reports.employee.percentTotalRevenue": "% du CA total",
    "Reports.employee.avgTicket": "Ticket moyen",
    "Reports.employee.servicesCount": "# Services",
    "Reports.employee.productsCount": "# Produits",
    "Reports.employee.unassigned": "Non assigné",
    "Reports.employee.servicePerformance": "Performance par service",
    "Reports.employee.totalSalesHeader": "Total ventes",
    "Reports.employee.servicesHeader": "# Services",
    "Reports.employee.avgPriceHeader": "Prix moyen",
    "Reports.employee.percentSalonRevenue": "% du CA salon",
    "Reports.employee.download": "Télécharger le rapport employés",
    "Reports.employee.sectionProductivity": "Section C : Métriques de PRODUCTIVITÉ",
    "Reports.employee.productivityTable": "Métriques de productivité par employé",
    "Reports.employee.workingDays": "Jours",
    "Reports.employee.hoursWorked": "Heures",
    "Reports.employee.clientsPerDay": "Nb. Client / Jour",
    "Reports.employee.clientsPerHour": "Nb. Client / Heure",
    "Reports.employee.servicesPerHour": "Moy. Service / Heure",
    "Reports.employee.servicesPerClient": "Moy. Service / Client",
    "Reports.employee.productsSoldPerClient": "Produit vendu / Client",
    "Reports.employee.rebookingRate": "% Pré-réservation",
    "Reports.employee.notAvailable": "N/A",
    "Reports.employee.noAppointmentData": "Aucune donnée de rendez-vous disponible pour cette période.",
    "Reports.downloadReport": "Télécharger le rapport",

    // Reports Module
    "Reports.title": "Rapports",
    "Reports.scrollLeft": "Faire défiler les filtres vers la gauche",
    "Reports.scrollRight": "Faire défiler les filtres vers la droite",
    "Reports.subtitle": "Générez et téléchargez vos rapports d'entreprise",
    "Reports.loading": "Chargement...",
    "Reports.error": "Une erreur est survenue lors du chargement de ce rapport. Veuillez réessayer.",
    "Reports.selectDates": "Sélectionner les dates",
    "Reports.startDate": "Date de début",
    "Reports.endDate": "Date de fin",
    "Reports.selectStartDate": "Sélectionner une date de début",
    "Reports.selectEndDate": "Sélectionner une date de fin",
    "Reports.continue": "Continuer",
    "Reports.cancel": "Annuler",
    "Reports.back": "Retour",
    "Reports.generating": "Génération en cours...",
    "Reports.downloadPDF": "Télécharger PDF",
    "Reports.exportCsv": "Exporter en CSV",
    "Reports.dateRequired": "Dates requises",
    "Reports.dateRequiredDesc": "Veuillez sélectionner une date de début et de fin pour générer le rapport.",
    "Reports.invalidDateRange": "Plage de dates invalide",
    "Reports.invalidDateRangeDesc": "La date de début doit être antérieure à la date de fin.",
    "Reports.reportReady": "Rapport prêt",
    "Reports.reportReadyDesc": "Le rapport est prêt pour l'impression/téléchargement.",
    "Reports.errorGenerating": "Erreur lors de la génération du rapport",
    "Reports.upgradeEnterprise": "Passez au plan Enterprise",
    "Reports.filterAll": "Tous les rapports",
    "Reports.filterSales": "Ventes",
    "Reports.filterFinance": "Financier",
    "Reports.filterAppointments": "Rendez-vous",
    "Reports.filterTeam": "Équipe",
    "Reports.filterClients": "Clients",
    "Reports.filterInventory": "Inventaire",
    "Reports.noReportsInCategory": "Aucun rapport disponible dans cette catégorie pour le moment",
    "Reports.payrollPermissionNote": "Les rapports de paie et de commissions demandent la permission Paie.",
    "Reports.comingSoon": "Bientôt disponible",
    "Reports.comingSoonDesc": "Ce rapport est en cours de développement et sera bientôt disponible.",
    "Reports.inDevelopment": "En développement",
    "Reports.enterpriseRequired": "Plan Enterprise requis",
    "Reports.enterpriseRequiredDesc": "Ce rapport est disponible avec le plan Enterprise.",
    "Reports.freshness.asOf": "Chiffres au {date}",
    "Reports.freshness.refresh": "Actualiser",
    "Reports.freshness.refreshing": "Actualisation…",
    "Reports.export.preparing": "Préparation…",
    "Reports.export.refreshFailed": "Les chiffres n'ont pas pu être relus, le fichier n'a donc pas été téléchargé. Veuillez réessayer.",
    "Reports.export.nothingToExport": "Il n'y a rien à exporter pour ces filtres.",
    "Reports.allMembers": "Tous les membres",
    "Reports.allProducts": "Tous les produits",
    "Reports.allCategories": "Toutes les catégories",
    "Reports.noCategory": "Sans catégorie",
    "Reports.allSuppliers": "Tous les fournisseurs",
    "Reports.noSupplier": "Sans fournisseur",
    "Reports.presetToday": "Journalier",
    "Reports.presetMonthToDate": "Début du mois jusqu'à ce jour",
    "Reports.presetLast7Days": "7 derniers jours",
    "Reports.presetLast30Days": "30 derniers jours",
    "Reports.presetLastMonth": "Mois dernier",
    "Reports.presetLast3Months": "Les 3 derniers mois",
    "Reports.presetLast6Months": "Les 6 derniers mois",
    "Reports.presetCustom": "Période personnalisée",
    // Récapitulatif des ventes — colonnes et libellés
    "Reports.salesSummary.type": "Type",
    "Reports.salesSummary.article": "Article",
    "Reports.salesSummary.qtySold": "Qté vendue",
    "Reports.salesSummary.grossSales": "Ventes brutes",
    "Reports.salesSummary.totalDiscounts": "Réductions totales",
    "Reports.salesSummary.refunds": "Remboursements",
    "Reports.salesSummary.netSales": "Ventes nettes",
    "Reports.salesSummary.taxes": "Taxes",
    "Reports.salesSummary.totalSales": "Total des ventes",
    "Reports.salesSummary.total": "Total",
    "Reports.salesSummary.service": "Services",
    "Reports.salesSummary.product": "Produit",
    "Reports.salesSummary.other": "Autre",
    "Reports.salesSummary.noData": "Aucune donnée de ventes pour cette période",
    "Reports.salesSummary.backToTypes": "Retour aux types",
    // Liste des ventes — colonnes et libellés
    "Reports.salesList.saleNumber": "N° de la vente",
    "Reports.salesList.saleDate": "Date de vente",
    "Reports.salesList.status": "Statut",
    "Reports.salesList.client": "Client",
    "Reports.salesList.itemsSold": "Articles vendus",
    "Reports.salesList.totalSales": "Total des ventes",
    "Reports.salesList.giftCard": "Carte cadeau",
    "Reports.salesList.autonomous": "Autonome",
    "Reports.salesList.amountDue": "Montant dû",
    "Reports.salesList.total": "Total",
    "Reports.salesList.noData": "Aucune vente pour cette période",
    "Reports.salesList.showing": "Affichage de",
    "Reports.salesList.of": "sur",
    "Reports.salesList.results": "résultats",
    // Journal des ventes détaillé
    "Reports.salesDetailedJournal.date": "Date",
    "Reports.salesDetailedJournal.saleNumber": "N° de la vente",
    "Reports.salesDetailedJournal.type": "Type",
    "Reports.salesDetailedJournal.item": "Article",
    "Reports.salesDetailedJournal.section": "Section",
    "Reports.salesDetailedJournal.client": "Client",
    "Reports.salesDetailedJournal.teamMember": "Membre de l'équipe",
    "Reports.salesDetailedJournal.grossSales": "Ventes brutes",
    "Reports.salesDetailedJournal.itemDiscounts": "Réductions sur l'article",
    "Reports.salesDetailedJournal.cartDiscounts": "Réductions sur le panier",
    "Reports.salesDetailedJournal.totalDiscounts": "Réductions totales",
    "Reports.salesDetailedJournal.refunds": "Remboursements",
    "Reports.salesDetailedJournal.netSales": "Ventes nettes",
    "Reports.salesDetailedJournal.netSalesTaxes": "Taxes sur les ventes nettes",
    "Reports.salesDetailedJournal.totalSales": "Total des ventes",
    "Reports.salesDetailedJournal.total": "Total",
    "Reports.salesDetailedJournal.noData": "Aucune vente pour cette période",
    "Reports.salesDetailedJournal.showing": "Affichage de",
    "Reports.salesDetailedJournal.of": "sur",
    "Reports.salesDetailedJournal.results": "résultats",
    "Reports.salesDetailedJournal.na": "N/A",
    "Reports.salesDetailedJournal.refundOf": "Remboursement de la facture",

    // Récapitulatif des remises
    "Reports.discountSummary.discountCategory": "Catégorie de réduction",
    "Reports.discountSummary.discountedItems": "Articles à prix réduit",
    "Reports.discountSummary.grossSales": "Ventes brutes",
    "Reports.discountSummary.itemDiscounts": "Réductions sur l'article",
    "Reports.discountSummary.cartDiscounts": "Réductions sur le panier",
    "Reports.discountSummary.totalDiscounts": "Réductions totales",
    "Reports.discountSummary.totalDiscountPercent": "% de réduction totale",
    "Reports.discountSummary.total": "Total",
    "Reports.discountSummary.noData": "Aucune réduction appliquée sur cette période",
    "Reports.discountSummary.manualDiscount": "Réduction manuelle",
    "Reports.taxSummary.taxType": "Type de taxe",
    "Reports.taxSummary.taxRate": "Taux de taxe",
    "Reports.taxSummary.itemsSold": "Articles vendus",
    "Reports.taxSummary.billedTaxes": "Taxes facturées",
    "Reports.taxSummary.adjustments": "Ajustements de la période",
    "Reports.taxSummary.netTaxes": "Taxes nettes",
    "Reports.taxSummary.total": "Total",
    "Reports.taxSummary.noData": "Aucune taxe trouvée pour cette période.",
    "Reports.taxSummary.byCategory.service": "Services",
    "Reports.taxSummary.byCategory.product": "Produits",
    "Reports.taxSummary.byCategory.other": "Autre",

    "Reports.financialSummary.sales": "Ventes",
    "Reports.financialSummary.grossSales": "Ventes brutes",
    "Reports.financialSummary.discounts": "Réductions",
    "Reports.financialSummary.refunds": "Remboursements/retours",
    "Reports.financialSummary.netSales": "Ventes nettes",
    "Reports.financialSummary.taxes": "Taxes",
    "Reports.financialSummary.totalSales": "Total des ventes",
    "Reports.financialSummary.giftCardSales": "Ventes de carte cadeau",
    "Reports.financialSummary.tips": "Pourboires",
    "Reports.financialSummary.greencircleFees": "Frais Green Circle",
    "Reports.financialSummary.totalOtherSales": "Total des autres ventes",
    "Reports.financialSummary.totalSalesPlusOther": "Total des ventes + autres ventes",
    "Reports.financialSummary.paymentsByPaidDate": "Paiements (selon la date de paiement)",
    "Reports.financialSummary.totalPayments": "Total des paiements",
    "Reports.financialSummary.noData": "Aucune donnée financière trouvée pour cette période.",

    "Reports.paymentsSummary.paymentMethod": "Mode de paiement",
    "Reports.paymentsSummary.paymentCount": "Nb de paiements",
    "Reports.paymentsSummary.amount": "Montant",
    "Reports.paymentsSummary.refundCount": "Nb de remboursements",
    "Reports.paymentsSummary.refunds": "Remboursements",
    "Reports.paymentsSummary.netPayments": "Paiements nets",
    "Reports.paymentsSummary.total": "Total",
    "Reports.paymentsSummary.noData": "Aucun paiement trouvé pour cette période.",

    "Reports.giftCardsList.code": "Code",
    "Reports.giftCardsList.saleNumber": "N° de la vente",
    "Reports.giftCardsList.purchasedBy": "Achetée par",
    "Reports.giftCardsList.status": "Statut",
    "Reports.giftCardsList.issueDate": "Date d'émission",
    "Reports.giftCardsList.expirationDate": "Date d'expiration",
    "Reports.giftCardsList.na": "N/A",
    "Reports.giftCardsList.total": "Total",
    "Reports.giftCardsList.giftCards": "cartes cadeaux",
    "Reports.giftCardsList.noData": "Aucune carte cadeau trouvée pour cette période.",
    "Reports.giftCardsList.showing": "Affichage de",
    "Reports.giftCardsList.of": "sur",
    "Reports.giftCardsList.results": "résultats",
    "Reports.giftCardsList.unknownCustomer": "Client inconnu",
    "Reports.itemType.service": "Service",
    "Reports.itemType.product": "Produit",
    "Reports.itemType.tip": "Pourboire",
    "Reports.itemType.discount": "Réduction",
    "Reports.itemType.gift_card_sale": "Carte cadeau",
    "Reports.itemType.gift_card_topup": "Recharge carte cadeau",
    "Reports.itemType.gift_card_redemption": "Utilisation carte cadeau",
    "Reports.itemType.other": "Autre",
    // Rapports ventes
    "Reports.salesSummaryTitle": "Récapitulatif des ventes",
    "Reports.salesSummaryDesc": "Quantité et montant des ventes, à l'exclusion des pourboires et des ventes de cartes cadeaux.",
    "Reports.salesByPeriodTitle": "Ventes par période",
    "Reports.salesByPeriodDesc": "Données de vente détaillées basées sur les périodes sélectionnées.",
    "Reports.salesByPeriod.noData": "Aucune donnée de vente pour cette période",
    "Reports.salesByPeriod.caHt": "CA HT",
    "Reports.salesByPeriod.tps": "TPS",
    "Reports.salesByPeriod.tvq": "TVQ",
    "Reports.salesByPeriod.caTtc": "CA TTC",
    "Reports.salesByPeriod.invoiceCount": "# factures",
    "Reports.salesByPeriod.avgTicket": "Panier moyen",
    "Reports.salesByPeriod.refunds": "Remboursements, taxes incluses",
    "Reports.salesByPeriod.byCategory": "Par catégorie d'article",
    "Reports.salesByPeriod.byPaymentMethodPaidDate": "Par mode de paiement (selon la date de paiement)",
    "Reports.salesByPeriod.byDay": "Ventilation quotidienne",
    "Reports.salesByPeriod.topEmployees": "Meilleurs vendeurs",
    "Reports.salesByPeriod.category.service": "Services",
    "Reports.salesByPeriod.category.product": "Produits",
    "Reports.salesByPeriod.category.tip": "Pourboires",
    "Reports.salesByPeriod.category.gift_card": "Cartes cadeaux",
    "Reports.salesByPeriod.category.discount": "Escomptes",
    "Reports.salesByPeriod.category.other": "Autres",
    "Reports.salesByPeriod.column.category": "Catégorie",
    "Reports.salesByPeriod.column.amount": "Montant",
    "Reports.salesByPeriod.column.percent": "%",
    "Reports.salesByPeriod.column.method": "Mode",
    "Reports.salesByPeriod.column.date": "Date",
    "Reports.salesByPeriod.column.invoiceCount": "# factures",
    "Reports.salesByPeriod.column.netHt": "CA HT",
    "Reports.salesByPeriod.column.tps": "TPS",
    "Reports.salesByPeriod.column.tvq": "TVQ",
    "Reports.salesByPeriod.column.totalTtc": "CA TTC",
    "Reports.salesByPeriod.column.rank": "Rang",
    "Reports.salesByPeriod.column.employee": "Employé",
    "Reports.salesListTitle": "Liste des ventes",
    "Reports.salesListDesc": "Liste complète de toutes les transactions de ventes.",
    "Reports.salesDetailedJournalTitle": "Journal des ventes détaillé",
    "Reports.salesDetailedJournalDesc": "Vue détaillée de chaque transaction de vente.",
    "Reports.giftCardsByPeriodTitle": "Cartes cadeaux par période",
    "Reports.giftCardsByPeriodDesc": "Données relatives aux ventes et à l'utilisation des cartes cadeaux basées sur les périodes sélectionnées.",
    "Reports.giftCardsByPeriod.noData": "Aucune carte cadeau pour cette période",
    "Reports.giftCardsByPeriod.issuedAmount": "Émises",
    "Reports.giftCardsByPeriod.usedAmount": "Utilisées",
    "Reports.giftCardsByPeriod.inCirculationAmount": "En circulation",
    "Reports.giftCardsByPeriod.expiredAmount": "Expirées",
    "Reports.giftCardsByPeriod.cards": "cartes",
    "Reports.giftCardsByPeriod.issuance": "Émission",
    "Reports.giftCardsByPeriod.usage": "Utilisation",
    "Reports.giftCardsByPeriod.inCirculation": "En circulation",
    "Reports.giftCardsByPeriod.column.date": "Date",
    "Reports.giftCardsByPeriod.column.code": "Code",
    "Reports.giftCardsByPeriod.column.customer": "Client",
    "Reports.giftCardsByPeriod.column.initialAmount": "Montant initial",
    "Reports.giftCardsByPeriod.column.expiration": "Expiration",
    "Reports.giftCardsByPeriod.column.sellingEmployee": "Vendue par",
    "Reports.giftCardsByPeriod.column.amount": "Montant utilisé",
    "Reports.giftCardsByPeriod.column.invoice": "Facture",
    "Reports.giftCardsByPeriod.column.employee": "Employé",
    "Reports.giftCardsByPeriod.column.currentBalance": "Solde actuel",
    "Reports.giftCardsByPeriod.column.issueDate": "Date d'émission",
    "Reports.giftCardsListTitle": "Liste des cartes cadeaux",
    "Reports.giftCardsListDesc": "Liste complète des cartes cadeaux émises et non utilisées.",
    "Reports.subscriptionsListTitle": "Liste des abonnements",
    "Reports.subscriptionsListDesc": "Liste complète des abonnements actifs.",
    "Reports.discountsSummaryTitle": "Récapitulatif des réductions",
    "Reports.discountsSummaryDesc": "Aperçu des réductions accordées et de leur impact sur les ventes.",
    "Reports.salesAnalysisTitle": "Analyse cross-sell",
    "Reports.salesAnalysisDesc": "Taux d'attachement produit et composition des factures par type.",
    "Reports.salesAnalysis.invoiceType": "Type de facture",
    "Reports.salesAnalysis.invoiceCount": "# factures",
    "Reports.salesAnalysis.percentInvoices": "% factures",
    "Reports.salesAnalysis.revenue": "CA total",
    "Reports.salesAnalysis.percentRevenue": "% du CA",
    "Reports.salesAnalysis.avgTicket": "Ticket moyen",
    "Reports.salesAnalysis.other": "Autre",
    "Reports.salesAnalysis.total": "Total",
    "Reports.salesAnalysis.serviceAndProduct": "Service + produit",
    "Reports.salesAnalysis.serviceOnly": "Service uniquement",
    "Reports.salesAnalysis.withProduct": "Produit uniquement",
    "Reports.salesAnalysis.attachmentRate": "Taux d'attachement produit",
    "Reports.salesAnalysis.noData": "Aucune donnée de vente pour cette période.",
    "Reports.taxesSummaryTitle": "Récapitulatif des taxes",
    "Reports.taxesSummaryDesc": "Récapitulatif de toutes les transactions sujettes à des taxes.",
    // Rapports finance
    "Reports.financialSummaryTitle": "Récapitulatif financier",
    "Reports.financialSummaryDesc": "Récapitulatif complet des ventes, paiements et passifs.",
    "Reports.paymentsSummaryTitle": "Récapitulatif des paiements",
    "Reports.paymentsSummaryDesc": "Paiements répartis par modes de paiement.",
    "Reports.financialTransactionsTitle": "Transactions financières",
    "Reports.financialTransactionsDesc": "Aperçu détaillé de toutes les opérations de paiement.",
    "Reports.financialTransactions.paymentDate": "Date du paiement",
    "Reports.financialTransactions.saleDate": "Date de vente",
    "Reports.financialTransactions.saleNumber": "N° de la vente",
    "Reports.financialTransactions.client": "Client",
    "Reports.financialTransactions.teamMember": "Membre de l'équipe",
    "Reports.financialTransactions.transactionType": "Type de transaction",
    "Reports.financialTransactions.paymentMethod": "Mode de paiement",
    "Reports.financialTransactions.externallySettled": "Réglé hors Flowcut",
    "Reports.financialTransactions.amount": "Montant",
    "Reports.financialTransactions.total": "Total",
    "Reports.totalLabel": "Total :",
    "Reports.financialTransactions.transactions": "transactions",
    "Reports.financialTransactions.noData": "Aucune transaction pour cette période.",
    "Reports.financialTransactions.showing": "Affichage de",
    "Reports.financialTransactions.of": "sur",
    "Reports.financialTransactions.results": "résultats",
    "Reports.taxList.saleNumber": "N° de la vente",
    "Reports.taxList.saleDate": "Date de vente",
    "Reports.taxList.type": "Type",
    "Reports.taxList.item": "Article",
    "Reports.taxList.category": "Catégorie",
    "Reports.taxList.teamMember": "Membre de l'équipe",
    "Reports.taxList.client": "Client",
    "Reports.taxList.paymentMethod": "Mode de paiement",
    "Reports.taxList.netSales": "Ventes nettes",
    "Reports.taxList.taxName": "Nom de la taxe",
    "Reports.taxList.taxRate": "Taux de taxe",
    "Reports.taxList.taxOnNetSales": "Taxes sur ventes nettes",
    "Reports.taxList.total": "Total",
    "Reports.taxList.rows": "lignes",
    "Reports.taxList.noData": "Aucune transaction avec taxes pour cette période.",
    "Reports.taxList.showing": "Affichage de",
    "Reports.taxList.of": "sur",
    "Reports.taxList.results": "résultats",
    "Reports.taxList.refundOf": "Remboursement de la facture",
    "Reports.taxList.typeRefund": "Remboursement",
    "Reports.taxList.typeChargeback": "Rétrofacturation",
    "Reports.cashFlowSummaryTitle": "Récapitulatif des flux de trésorerie",
    "Reports.cashFlowSummaryDesc": "Aperçu des entrées et des sorties de fonds.",
    "Reports.cashFlowStatementTitle": "Relevé des flux de trésorerie",
    "Reports.cashFlowStatementDesc": "Journal détaillé des flux de trésorerie sur une période sélectionnée.",
    "Reports.serviceFeesTitle": "Frais de service",
    "Reports.serviceFeesDesc": "Ventilation des revenus des frais de service.",
    "Reports.liabilitiesSummaryTitle": "Récapitulatif des passifs",
    "Reports.liabilitiesSummaryDesc": "Aperçu des passifs de l'entreprise par type. Ce rapport exclut les cartes cadeaux impayées et annulées.",
    "Reports.liabilityActivitiesTitle": "Activités liées au passif",
    "Reports.liabilityActivitiesDesc": "Aperçu détaillé des opérations liées au passif.",
    "Reports.prepaymentsByPeriodTitle": "Prépaiements par période",
    "Reports.prepaymentsByPeriodDesc": "Analyse des prépaiements sur une période sélectionnée.",
    "Reports.prepaymentListTitle": "Liste des prépaiements",
    "Reports.prepaymentListDesc": "Liste complète de tous les prépaiements.",
    "Reports.taxesListTitle": "Liste des taxes",
    "Reports.taxesListDesc": "Liste complète de toutes les transactions contenant des taxes.",
    "Reports.greencircleTitle": "Rapport Green Circle",
    "Reports.greencircleDesc": "Total des frais Green Circle facturés pendant la période sélectionnée.",
    "Reports.greencircleNoData": "Aucun frais Green Circle pour cette période",
    "Reports.greencircleDate": "Date",
    "Reports.greencircleInvoiceCount": "Nombre de factures",
    "Reports.greencircleFee": "Frais",
    "Reports.greencircleTotal": "Total des frais Green Circle",
    // Rapports rendez-vous
    "Reports.appointmentsSummaryTitle": "Récapitulatif des rendez-vous",
    "Reports.appointmentsSummaryDesc": "Aperçu général des habitudes et des tendances en matière de rendez-vous, y compris les annulations et rendez-vous manqués.",
    "Reports.apptSummary.member": "Membre",
    "Reports.apptSummary.appointments": "Rendez-vous",
    "Reports.apptSummary.services": "Prestations",
    "Reports.apptSummary.totalAmount": "Montant total du RDV",
    "Reports.apptSummary.avgAmount": "Montant moy. du RDV",
    "Reports.apptSummary.onlinePercent": "% en ligne",
    "Reports.apptSummary.cancelledPercent": "% annulés",
    "Reports.apptSummary.noShowPercent": "% manqués",
    "Reports.apptSummary.totalClients": "Nombre total de clients",
    "Reports.apptSummary.newClients": "Nouveaux clients",
    "Reports.apptSummary.newClientPercent": "% nouveaux clients",
    "Reports.apptSummary.recurringPercent": "% clients récurrents",
    "Reports.apptSummary.total": "Total",
    "Reports.apptSummary.noData": "Aucune donnée de rendez-vous pour cette période.",
    "Reports.apptList.ref": "Réf. RDV",
    "Reports.apptList.client": "Client",
    "Reports.apptList.teamMember": "Membre de l'équipe",
    "Reports.apptList.resource": "Ressource",
    "Reports.apptList.status": "Statut",
    "Reports.apptList.createdDate": "Date de création",
    "Reports.apptList.scheduledDate": "Date programmée",
    "Reports.apptList.cancelledDate": "Date d'annulation",
    "Reports.apptList.category": "Catégorie",
    "Reports.apptList.service": "Prestation",
    "Reports.apptList.duration": "Durée (min)",
    "Reports.apptList.timeSlot": "Créneau RDV",
    "Reports.apptList.createdBy": "Créé par",
    "Reports.apptList.cancelledBy": "Annulé par",
    "Reports.apptList.cancellationReason": "Motif d'annulation",
    "Reports.apptList.netSales": "Ventes nettes",
    "Reports.apptList.total": "Total",
    "Reports.apptList.noData": "Aucun rendez-vous pour cette période.",
    "Reports.apptList.salon": "Salon",
    "Reports.apptList.online": "En ligne",
    "Reports.apptList.voiceAI": "IA vocale",
    "Reports.apptList.statusConfirmed": "Confirmé",
    "Reports.apptList.statusCompleted": "Complété",
    "Reports.apptList.statusCancelled": "Annulé",
    "Reports.apptList.statusNoShow": "Manqué",
    "Reports.apptList.unassigned": "Non assigné",
    "Reports.appointmentsListTitle": "Liste des rendez-vous",
    "Reports.appointmentsListDesc": "Liste complète des rendez-vous programmés.",
    "Reports.cancelledMissedTitle": "Rendez-vous annulés et manqués",
    "Reports.cancelledMissedDesc": "Données sur les rendez-vous annulés et manqués.",
    "Reports.voiceAiChannelTitle": "Réservations IA vocale",
    "Reports.voiceAiChannelDesc": "Réservations, reports, annulations et qualité d'appels de l'assistant vocal IA.",
    "Reports.voiceAiChannel.bookings": "Réservations IA",
    "Reports.voiceAiChannel.reschedules": "Reports IA",
    "Reports.voiceAiChannel.cancellations": "Annulations IA",
    "Reports.voiceAiChannel.totalCalls": "Total d'appels",
    "Reports.voiceAiChannel.successRate": "Taux de succès",
    "Reports.voiceAiChannel.avgDuration": "Durée moyenne",
    "Reports.voiceAiChannel.bookingsSection": "Réservations",
    "Reports.voiceAiChannel.callsSection": "Appels",
    "Reports.voiceAiChannel.noData": "Aucune donnée IA sur cette période",
    "Reports.voiceAiChannel.csvMetric": "Métrique",
    "Reports.voiceAiChannel.csvValue": "Total",
    "Reports.directBookingChannelTitle": "Réservation directe (QR + Lien)",
    "Reports.directBookingChannelDesc": "Visites par QR/lien et taux de conversion en rendez-vous.",
    "Reports.directBookingChannel.totalVisits": "Visites directes (toutes sources)",
    "Reports.directBookingChannel.conversions": "Conversions",
    "Reports.directBookingChannel.conversionRate": "Taux de conversion",
    "Reports.directBookingChannel.noData": "Aucune visite directe sur cette période",
    "Reports.directBookingChannel.csvMetric": "Métrique",
    "Reports.directBookingChannel.csvValue": "Total",
    "Reports.cancelledMissed.reason": "Motif",
    "Reports.cancelledMissed.count": "Nb de rendez-vous",
    "Reports.cancelledMissed.percent": "% du total",
    "Reports.cancelledMissed.amount": "Montant",
    "Reports.cancelledMissed.total": "Total",
    "Reports.cancelledMissed.noData": "Aucun rendez-vous annulé ou manqué pour cette période.",
    "Reports.cancelledMissed.byClient": "Annulé par le client",
    "Reports.cancelledMissed.bySalon": "Annulé par le salon",
    "Reports.cancelledMissed.byVoiceAi": "Annulé par l'assistant vocal",
    "Reports.cancelledMissed.noReason": "Annulé (aucun motif)",
    "Reports.cancelledMissed.noShow": "Rendez-vous manqué",
    "Reports.waitlistDetailsTitle": "Renseignements sur la liste d'attente",
    "Reports.waitlistDetailsDesc": "Vue détaillée des clients inscrits sur la liste d'attente.",
    "Reports.waitlistSummaryTitle": "Récapitulatif de la liste d'attente",
    "Reports.waitlistSummaryDesc": "Aperçu des tendances et des modèles ressortant de la liste d'attente, y compris les rendez-vous pris et les inscriptions expirées.",
    // Rapports équipe
    "Reports.workHoursActivityTitle": "Activité liée aux horaires de travail",
    "Reports.workHoursActivityDesc": "Vue détaillée des heures travaillées, des plages horaires et des feuilles de temps des membres de l'équipe.",
    "Reports.breakActivityTitle": "Activité liée aux pauses",
    "Reports.breakActivityDesc": "Vue détaillée des pauses des membres de l'équipe.",
    "Reports.attendanceSummaryTitle": "Récapitulatif des présences",
    "Reports.attendanceSummaryDesc": "Aperçu de la ponctualité et de la présence des membres de l'équipe pour leurs plages horaires.",
    "Reports.salaryDetailsTitle": "Détail des salaires",
    "Reports.salaryDetailsDesc": "Aperçu détaillé des salaires versés aux membres de l'équipe dans tous les établissements.",
    "Reports.salarySummaryTitle": "Récapitulatif des salaires",
    "Reports.salarySummaryDesc": "Aperçu des salaires gagnés par les membres de l'équipe.",
    "Reports.feeDeductionsHistoryTitle": "Historique des déductions de frais",
    "Reports.feeDeductionsHistoryDesc": "Liste complète des frais déduits de la rémunération des membres de l'équipe.",
    "Reports.feeDeductionsSummaryTitle": "Récapitulatif des déductions de frais",
    "Reports.feeDeductionsSummaryDesc": "Aperçu des frais déduits des rémunérations par membres de l'équipe, par établissements et par articles vendus.",
    "Reports.payrollSummaryTitle": "Récapitulatif des paies",
    "Reports.payrollSummaryDesc": "Aperçu de la rémunération des membres de l'équipe.",
    "Reports.payrollSummary.noData": "Aucune paie finalisée trouvée pour cette période. Finalisez les paies dans le module Paie pour voir les données ici.",
    "Reports.payrollSummary.overview": "Vue d'ensemble",
    "Reports.payrollSummary.revenue": "Revenus",
    "Reports.payrollSummary.salaries": "Salaires",
    "Reports.payrollSummary.commissions": "Commissions",
    "Reports.payrollSummary.tips": "Pourboires",
    "Reports.payrollSummary.deductions": "Déductions",
    "Reports.payrollSummary.totalPay": "Paie totale",
    "Reports.payrollSummary.salariesSection": "Salaires",
    "Reports.payrollSummary.hourlyPay": "Rémunération horaire",
    "Reports.payrollSummary.hourlyRate": "Taux horaire",
    "Reports.payrollSummary.regularHoursPaid": "Heures normales rémunérées",
    "Reports.payrollSummary.overtimePay": "Rémunération des heures supplémentaires",
    "Reports.payrollSummary.overtimeRate": "Taux des heures supplémentaires",
    "Reports.payrollSummary.overtimeHoursPaid": "Heures supplémentaires rémunérées",
    "Reports.payrollSummary.totalPaidHoursWorked": "Total des heures travaillées rémunérées",
    "Reports.payrollSummary.fixedSalary": "Salaire fixe",
    "Reports.payrollSummary.totalSalaries": "Total des salaires",
    "Reports.payrollSummary.commissionsSection": "Commissions",
    "Reports.payrollSummary.serviceRevenue": "Revenus de services",
    "Reports.payrollSummary.serviceCommission": "Commission de services",
    "Reports.payrollSummary.productRevenue": "Revenus de produits",
    "Reports.payrollSummary.productCommission": "Commission de produits",
    "Reports.payrollSummary.totalCommissions": "Total des commissions",
    "Reports.payrollSummary.tipsSection": "Pourboires",
    "Reports.payrollSummary.totalTips": "Total des pourboires",
    "Reports.payrollSummary.deductionsSection": "Déductions",
    "Reports.payrollSummary.dailyDeductions": "Déductions quotidiennes",
    "Reports.payrollSummary.namedDeductions": "Déductions nommées",
    "Reports.payrollSummary.revenuePercentDeduction": "Déduction % revenus",
    "Reports.payrollSummary.totalDeductions": "Total des déductions",
    "Reports.payrollSummary.total": "Total",
    "Reports.payrollSummary.grandTotal": "Total général",
    "Reports.scheduledShiftsTitle": "Plages horaires programmées",
    "Reports.scheduledShiftsDesc": "Affichage détaillé des plages horaires programmées des membres de l'équipe.",
    "Reports.scheduledShifts.noData": "Aucune plage horaire programmée trouvée pour cette période.",
    "Reports.scheduledShifts.teamMember": "Membre de l'équipe",
    "Reports.scheduledShifts.establishment": "Établissement",
    "Reports.scheduledShifts.dayOfWeek": "Jour de la semaine",
    "Reports.scheduledShifts.date": "Date",
    "Reports.scheduledShifts.scheduledStart": "Début prévu",
    "Reports.scheduledShifts.scheduledEnd": "Fin prévue",
    "Reports.scheduledShifts.duration": "Durée",
    "Reports.scheduledShifts.total": "Total",
    "Reports.hoursWorkedSummaryTitle": "Récapitulatif des heures travaillées",
    "Reports.hoursWorkedSummaryDesc": "Aperçu des heures travaillées et de la productivité.",
    "Reports.hoursWorkedSummary.teamMember": "Membre de l'équipe",
    "Reports.hoursWorkedSummary.scheduled": "Programmé",
    "Reports.hoursWorkedSummary.blocked": "Bloqué",
    "Reports.hoursWorkedSummary.available": "Disponible",
    "Reports.hoursWorkedSummary.booked": "Réservé",
    "Reports.hoursWorkedSummary.notBooked": "Non réservé",
    "Reports.hoursWorkedSummary.occupancy": "% occupation",
    "Reports.hoursWorkedSummary.total": "Total",
    "Reports.hoursWorkedSummary.noData": "Aucune donnée d'heures travaillées pour cette période.",
    "Reports.teamAbsenceSummaryTitle": "Récapitulatif des absences de l'équipe",
    "Reports.teamAbsenceSummaryDesc": "Aperçu détaillé des moments d'absence des membres de l'équipe.",
    "Reports.tipsSummaryTitle": "Récapitulatif des pourboires",
    "Reports.tipsSummaryDesc": "Analyse des montants de pourboires perçus.",
    "Reports.tipsSummary.teamMember": "Membre de l'équipe",
    "Reports.tipsSummary.tipsCollected": "Pourboires perçus",
    "Reports.tipsSummary.tipsRefunded": "Pourboires remboursés",
    "Reports.tipsSummary.totalTips": "Total des pourboires",
    "Reports.tipsSummary.total": "Total",
    "Reports.tipsSummary.noData": "Aucune donnée de pourboires trouvée pour cette période.",
    "Reports.tipsDetails.saleNumber": "N° de la vente",
    "Reports.tipsDetails.saleDate": "Date de vente",
    "Reports.tipsDetails.teamMember": "Membre de l'équipe",
    "Reports.tipsDetails.client": "Client",
    "Reports.tipsDetails.establishment": "Établissement",
    "Reports.tipsDetails.transactionType": "Type de transaction",
    "Reports.tipsDetails.channel": "Canal",
    "Reports.tipsDetails.paymentMethod": "Mode de paiement",
    "Reports.tipsDetails.tipsCollected": "Pourboires perçus",
    "Reports.tipsDetails.total": "Total",
    "Reports.tipsDetails.sale": "Vente",
    "Reports.tipsDetails.atPointOfSale": "Au moment du paiement",
    "Reports.tipsDetails.noData": "Aucune donnée détaillée de pourboires trouvée pour cette période.",
    "Reports.tipsDetailsTitle": "Renseignements sur les pourboires",
    "Reports.tipsDetailsDesc": "Ventilation complète de tous les pourboires reçus.",
    "Reports.commissionActivityTitle": "Activité liée aux commissions",
    "Reports.commissionActivityDesc": "Liste complète de toutes les ventes avec les commissions payables.",
    "Reports.commissionSummaryTitle": "Résumé des commissions",
    "Reports.commissionSummaryDesc": "Aperçu des commissions gagnées par les membres de l'équipe, par établissements et par articles vendus.",
    "Reports.commissionActivity.saleNumber": "N° de la vente",
    "Reports.commissionActivity.saleDate": "Date de vente",
    "Reports.commissionActivity.client": "Client",
    "Reports.commissionActivity.type": "Type",
    "Reports.commissionActivity.typeService": "Service",
    "Reports.commissionActivity.typeProduct": "Produit",
    "Reports.commissionActivity.article": "Article",
    "Reports.commissionActivity.teamMember": "Membre de l'équipe",
    "Reports.commissionActivity.grossSales": "Ventes brutes",
    "Reports.commissionActivity.taxes": "Taxes",
    "Reports.commissionActivity.discounts": "Réductions",
    "Reports.commissionActivity.cost": "Coût",
    "Reports.commissionActivity.commissionBase": "Base de commission",
    "Reports.commissionActivity.commission": "Commission",
    "Reports.commissionActivity.commissionPercent": "% commission",
    "Reports.commissionActivity.total": "Total",
    "Reports.commissionActivity.noData": "Aucune donnée d'activité de commission trouvée pour cette période.",
    // Commission par paliers (tâche 9 du chantier commissions-paliers)
    "Reports.commissionActivity.tiered": "Paliers",
    "Reports.commissionActivity.atCumulative": "au cumul",
    "Reports.commissionActivity.outsideCumulative": "hors cumul, facture non soldée",
    "Reports.commissionActivity.tierAmountHidden": "Montant par paliers non affiché : vos accès ne couvrent pas toutes les données de rémunération nécessaires à son calcul pour ce membre de l'équipe.",
    "Reports.commissionActivity.tierAmountPending": "Montant par paliers en cours de chargement.",
    "Reports.commissionActivity.amountReserved": "Réservé",
    "Reports.commissionActivity.amountReservedHint": "Commission non affichée : vous ne pouvez consulter que votre propre rémunération.",
    "Reports.commissionActivity.tierBlockTitle": "Commission par paliers",
    "Reports.commissionActivity.tierBlockNote": "Calculée par le moteur de paie sur les seules ventes payées de la période choisie, comme le talon : une facture partiellement payée n'y entre pas. Le talon de paie reste la seule référence du montant versé.",
    "Reports.commissionActivity.tierCategoryService": "Services",
    "Reports.commissionActivity.tierCategoryProduct": "Produits",
    "Reports.commissionActivity.tierColumnBracket": "Tranche du cumul",
    "Reports.commissionActivity.tierColumnBase": "Montant",
    "Reports.commissionActivity.tierColumnRate": "Taux",
    "Reports.commissionActivity.tierBelowThreshold": "Sous le seuil, 0 à {to}",
    "Reports.commissionActivity.tierBand": "Palier {n}, {from} à {to}",
    "Reports.commissionActivity.tierBandOpen": "Palier {n}, à partir de {from}",
    "Reports.commissionActivity.tierCumulative": "Cumul de la période",
    "Reports.commissionActivity.tierTotal": "Commission par paliers de la période",
    "Reports.commissionSummary.tieredBadge": "Paliers",
    "Reports.commissionSummary.tierAmountHidden": "Montant par paliers non affiché : vos accès ne couvrent pas toutes les données de rémunération nécessaires à son calcul pour ce membre de l'équipe.",
    "Reports.commissionSummary.tierAmountPending": "Montant par paliers en cours de chargement.",
    "Reports.commissionSummary.amountReserved": "Réservé",
    "Reports.commissionSummary.amountReservedHint": "Commission non affichée : vous ne pouvez consulter que votre propre rémunération.",
    "Reports.commissionSummary.tieredRateHint": "Taux indicatif : il rapporte la commission calculée par le moteur de paie à la base de commission de ce rapport.",
    "Reports.commissionSummary.tieredBadgeHint": "Ce membre de l'équipe est payé selon une échelle par paliers appliquée au cumul de la période. Pour ses catégories en paliers, la commission affichée vient du moteur de paie, sur les seules ventes payées de la période choisie. Le talon de paie reste la seule référence du montant versé.",
    "Reports.commissionSummary.teamMember": "Membre de l'équipe",
    "Reports.commissionSummary.qtySold": "Qté vendue",
    "Reports.commissionSummary.itemsSold": "Articles vendus",
    "Reports.commissionSummary.grossSales": "Ventes brutes",
    "Reports.commissionSummary.refunds": "Remboursements",
    "Reports.commissionSummary.taxes": "Taxes",
    "Reports.commissionSummary.discounts": "Réductions",
    "Reports.commissionSummary.costs": "Coûts",
    "Reports.commissionSummary.commissionBase": "Base de commission",
    "Reports.commissionSummary.commission": "Commission",
    "Reports.commissionSummary.commissionPercent": "% commission",
    "Reports.commissionSummary.total": "Total",
    "Reports.commissionSummary.noData": "Aucune donnée de résumé de commission trouvée pour cette période.",
    "Reports.teamProductivityTitle": "Productivité de l'équipe",
    "Reports.teamProductivityDesc": "Métriques de productivité par membre de l'équipe : clients, services, produits et taux de reprise.",
    "Reports.teamProductivity.teamMember": "Membre de l'équipe",
    "Reports.teamProductivity.workingDays": "Jours travaillés",
    "Reports.teamProductivity.hoursWorked": "Heures travaillées",
    "Reports.teamProductivity.clientsPerDay": "Clients/jour",
    "Reports.teamProductivity.clientsPerHour": "Clients/heure",
    "Reports.teamProductivity.servicesPerHour": "Services/heure",
    "Reports.teamProductivity.servicesPerClient": "Services/client",
    "Reports.teamProductivity.productsPerClient": "Produits/client",
    "Reports.teamProductivity.prebookingRate": "Pré-réservation",
    "Reports.teamProductivity.total": "Total",
    "Reports.teamProductivity.na": "N/D",
    "Reports.teamProductivity.noData": "Aucune donnée de productivité trouvée pour cette période.",
    "Reports.teamProductivity.sourceLabel": "Source des jours/heures",
    "Reports.teamProductivity.sourceSchedule": "Horaire planifié",
    "Reports.teamProductivity.sourceTimesheet": "Feuilles de temps (paie)",
    "Reports.teamProductivity.sourceHelp": "Détermine d'où proviennent « Jours travaillés » et « Heures travaillées ». « Horaire planifié » utilise les heures d'ouverture configurées, avec les horaires exceptionnels appliqués et les blocages retranchés. Disponible pour tous les forfaits. « Feuilles de temps » utilise les pointages réels (module de paie). Avec un horaire de salon partagé (sans horaires individuels), tous les employés affichent les mêmes heures planifiées.",
    "Reports.teamProductivity.sourceSaveFailed": "Impossible d'enregistrer le réglage de source.",
    // Rapports clients
    "Reports.clientsSummaryTitle": "Récapitulatif des clients",
    "Reports.clientsSummaryDesc": "Aperçu des clients nouveaux et récurrents avec rendez-vous et des clients de passage dans la période sélectionnée.",
    "Reports.clientsSummary.noData": "Aucun client avec rendez-vous pour cette période",
    "Reports.clientsSummary.uniqueClients": "Clients uniques",
    "Reports.clientsSummary.newClients": "Nouveaux",
    "Reports.clientsSummary.returningClients": "Récurrents",
    "Reports.clientsSummary.retentionRate": "Taux de rétention",
    "Reports.clientsSummary.byEmployee": "Par employé",
    "Reports.clientsSummary.byPeriod": "Évolution temporelle",
    "Reports.clientsSummary.topClients": "Meilleurs clients",
    "Reports.clientsSummary.column.employee": "Employé",
    "Reports.clientsSummary.column.uniqueClients": "Clients uniques",
    "Reports.clientsSummary.column.newClients": "Nouveaux",
    "Reports.clientsSummary.column.returningClients": "Récurrents",
    "Reports.clientsSummary.column.period": "Période",
    "Reports.clientsSummary.column.rank": "Rang",
    "Reports.clientsSummary.column.customer": "Client",
    "Reports.clientsSummary.column.appointmentCount": "# rendez-vous",
    "Reports.clientsSummary.column.revenue": "Revenu",
    "Reports.clientRetentionTitle": "Rétention client par employé",
    "Reports.clientRetentionDesc": "Compare la période sélectionnée avec la précédente : pour chaque employé, combien de ses clients sont revenus, avec qui, et combien sont perdus.",
    "Reports.clientRetention.noData": "Aucun employé trouvé pour cette période",
    "Reports.clientRetention.filter.currentPeriod": "Période actuelle",
    "Reports.clientRetention.filter.previousPeriod": "Période précédente",
    "Reports.clientRetention.group.previousPeriod": "Période précédente",
    "Reports.clientRetention.group.returnedSalonOrEmployee": "Clients revenus avec salon et/ou employé",
    "Reports.clientRetention.group.returnedWithEmployee": "Clients revenus avec l'employé",
    "Reports.clientRetention.group.returnedSalonOnly": "Clients revenus avec salon",
    "Reports.clientRetention.column.employee": "Employé",
    "Reports.clientRetention.column.active": "Actif",
    "Reports.clientRetention.column.totalClients": "Total clients",
    "Reports.clientRetention.column.pctClients": "% Clients",
    "Reports.clientRetention.column.newClients": "Nouveaux clients",
    "Reports.clientRetention.column.pctNewClients": "% Nouveaux clients",
    "Reports.clientRetention.column.existingClients": "Clients existants",
    "Reports.clientRetention.column.pctExistingClients": "% Clients existants",
    "Reports.clientRetention.column.newPlusExisting": "Nouveaux + existants",
    "Reports.clientRetention.column.pctNewPlusExisting": "% Nouveaux + existants",
    "Reports.clientRetention.column.lostClients": "Clients perdus",
    "Reports.clientRetention.column.pctLostClients": "% Clients perdus",
    "Reports.clientRetention.column.returnedClients": "Clients revenus",
    "Reports.clientRetention.column.pctReturnedClients": "% Clients revenus",
    "Reports.clientsListTitle": "Liste des clients",
    "Reports.clientsListDesc": "Liste complète de tous les clients actifs.",
    "Reports.clientsList.client": "Client",
    "Reports.clientsList.phone": "Numéro de portable",
    "Reports.clientsList.email": "E-mail",
    "Reports.clientsList.addedOn": "Ajouté le",
    "Reports.clientsList.firstAppointment": "Premier RDV",
    "Reports.clientsList.lastAppointment": "Dernier RDV",
    "Reports.clientsList.clientType": "Type de client",
    "Reports.clientsList.total": "Total",
    "Reports.clientsList.noData": "Aucun client n'est venu durant cette période.",
    "Reports.clientsList.clientTypeNew": "Nouveau",
    "Reports.clientsList.clientTypeReturning": "Récurrent",
    "Reports.clientsList.clientTypeLoyal": "Fidèle",
    "Reports.clientsList.clientTypeFlagged": "Signalé",
    "Reports.clientsList.showing": "Affichage de",
    "Reports.clientsList.of": "sur",
    "Reports.clientsList.results": "résultats",
    "Reports.byGenderTitle": "Répartition de la clientèle par sexe",
    "Reports.byGenderDesc": "Comment se répartit votre clientèle active selon le sexe déclaré.",
    "Reports.byLanguageTitle": "Langues privilégiées de la clientèle",
    "Reports.byLanguageDesc": "Les langues de communication préférées de vos clients actifs.",
    "Reports.bySourceTitle": "Canaux d'acquisition de la clientèle",
    "Reports.bySourceDesc": "Par quels canaux vos clients actifs vous ont découvert.",
    "Reports.byEmployeeTitle": "Préférences d'employé de la clientèle",
    "Reports.byEmployeeDesc": "Quels membres de l'équipe vos clients actifs préfèrent.",
    "Reports.notifFailuresTitle": "Échecs de notification",
    "Reports.notifFailuresDesc": "Clients non joints (SMS non délivré ou ligne fixe)",
    "Reports.notifFailures.date": "Date",
    "Reports.notifFailures.client": "Client",
    "Reports.notifFailures.channel": "Canal",
    "Reports.notifFailures.reason": "Raison",
    "Reports.notifFailures.channelValue.sms": "SMS",
    "Reports.notifFailures.channelValue.email": "Courriel",
    "Reports.notifFailures.reasonValue.undelivered": "Non délivré",
    "Reports.notifFailures.reasonValue.failed": "Échec d'envoi",
    "Reports.notifFailures.reasonValue.landline_no_sms": "Ligne fixe : pas de SMS",
    "Reports.notifFailures.total": "Total",
    "Reports.notifFailures.empty": "Aucun échec de notification pour cette période",
    "Reports.breakdown.category": "Catégorie",
    "Reports.breakdown.count": "Nb clients",
    "Reports.breakdown.pct": "%",
    "Reports.breakdown.avgBasket": "Panier moyen",
    "Reports.breakdown.avgVisits": "Visites moy.",
    "Reports.breakdown.total": "Total",
    "Reports.breakdown.notProvided": "Non renseigné",
    "Reports.breakdown.noPreferredEmployee": "Aucun employé préféré",
    "Reports.genderValue.homme": "Homme",
    "Reports.genderValue.femme": "Femme",
    "Reports.genderValue.autre": "Autre",
    "Reports.genderValue.prefere_ne_pas_dire": "Préfère ne pas dire",
    "Reports.languageValue.fr": "Français",
    "Reports.languageValue.en": "Anglais",
    "Reports.sourceValue.google": "Google",
    "Reports.sourceValue.social": "Réseaux sociaux",
    "Reports.sourceValue.referral": "Référence",
    "Reports.sourceValue.walkin": "Passage spontané",
    "Reports.sourceValue.online_booking": "Réservation en ligne",
    "Reports.sourceValue.competitor": "Ancien client (concurrent)",
    "Reports.sourceValue.other": "Autre",
    "Reports.clientDataTitle": "Données sur les clients",
    "Reports.clientDataDesc": "Étudiez en détail le comportement et les préférences de chaque client.",
    "Reports.clientData.client": "Client",
    "Reports.clientData.phone": "Téléphone",
    "Reports.clientData.email": "Courriel",
    "Reports.clientData.addedOn": "Ajouté le",
    "Reports.clientData.firstAppointment": "Premier RDV",
    "Reports.clientData.lastAppointment": "Dernier RDV",
    "Reports.clientData.totalAppointments": "Total des RDV",
    "Reports.clientData.totalSpent": "Montant total",
    "Reports.clientData.clientType": "Type de client",
    "Reports.clientData.total": "Total",
    "Reports.clientData.clients": "clients",
    "Reports.clientData.noData": "Aucune donnée client pour cette période.",
    "Reports.clientData.showing": "Affichage de",
    "Reports.clientData.of": "sur",
    "Reports.clientData.results": "résultats",
    // Rapports inventaire
    "Reports.availableStockTitle": "Stock disponible",
    "Reports.availableStockDesc": "Statut actuel et quantité des articles en stock.",
    "Reports.availableStock.brand": "Marque",
    "Reports.availableStock.sku": "SKU principal",
    "Reports.availableStock.product": "Produit",
    "Reports.availableStock.stock": "Stock disponible",
    "Reports.availableStock.totalCost": "Coût total",
    "Reports.availableStock.avgCost": "Coût moy.",
    "Reports.availableStock.retailPrice": "Prix au détail",
    "Reports.availableStock.retailAmount": "Montant des ventes au détail",
    "Reports.availableStock.margin": "% marge",
    "Reports.availableStock.lowThreshold": "Seuil de stock faible",
    "Reports.availableStock.total": "Total",
    "Reports.availableStock.noData": "Aucun produit trouvé.",
    "Reports.availableStock.live": "En direct",
    "Reports.availableStock.asOfDay": "Stock au {date} à 8 h 00",
    "Reports.availableStock.currentPricesNote": "Coûts et prix de vente actuels",
    "Reports.availableStock.dayAt8": "{date}, 8 h 00",
    "Reports.stockMovementsSummaryTitle": "Récapitulatif des mouvements de stock",
    "Reports.stockMovementsSummaryDesc": "Récapitulatif des entrées et sorties de stock.",
    "Reports.stockMovements.brand": "Marque",
    "Reports.stockMovements.sku": "SKU principal",
    "Reports.stockMovements.product": "Produit",
    "Reports.stockMovements.initialStock": "Stock initial",
    "Reports.stockMovements.received": "Reçu",
    "Reports.stockMovements.sold": "Vendu",
    "Reports.stockMovements.adjusted": "Ajusté",
    "Reports.stockMovements.internalUse": "Usage interne",
    "Reports.stockMovements.losses": "Pertes",
    "Reports.stockMovements.returns": "Retours",
    "Reports.stockMovements.finalStock": "Stock final",
    "Reports.stockMovements.total": "Total",
    "Reports.stockMovements.noData": "Aucun mouvement de stock pour cette période.",
    "Reports.stockMovementsJournalTitle": "Journal des mouvements de stock",
    "Reports.stockMovementsJournalDesc": "Journal détaillé de tous les mouvements de stocks.",
    "Reports.internalUseLossTitle": "Consommation et pertes",
    "Reports.internalUseLossDesc": "Produit consommé par le salon ou perdu, valorisé au coût",
    "Reports.internalUseLoss.brand": "Marque",
    "Reports.internalUseLoss.sku": "SKU",
    "Reports.internalUseLoss.product": "Produit",
    "Reports.internalUseLoss.internalQty": "Usage interne (qté)",
    "Reports.internalUseLoss.internalValue": "Valeur usage",
    "Reports.internalUseLoss.lossQty": "Pertes (qté)",
    "Reports.internalUseLoss.lossValue": "Valeur pertes",
    "Reports.internalUseLoss.totalQty": "Total sorti",
    "Reports.internalUseLoss.totalValue": "Valeur totale",
    "Reports.internalUseLoss.total": "Total",
    "Reports.internalUseLoss.noData": "Aucune consommation ni perte sur cette période",
    "Reports.stockJournal.date": "Date",
    "Reports.stockJournal.brand": "Marque",
    "Reports.stockJournal.sku": "SKU",
    "Reports.stockJournal.product": "Produit",
    "Reports.stockJournal.movementType": "Motif de l'ajust.",
    "Reports.stockJournal.reference": "Réf. de l'ajustement",
    "Reports.stockJournal.quantity": "Qté",
    "Reports.stockJournal.cost": "Coût",
    "Reports.stockJournal.avgCost": "Coût moy.",
    "Reports.stockJournal.total": "Total",
    "Reports.stockJournal.noData": "Aucun mouvement de stock pour cette période.",
    "Reports.stockJournal.type.transfer": "Transfert",
    "Reports.productsListTitle": "Liste des produits",
    "Reports.productsListDesc": "Liste complète de tous les produits.",
    "Reports.productsList.sku": "SKU principal",
    "Reports.productsList.product": "Produit",
    "Reports.productsList.category": "Catégorie",
    "Reports.productsList.barcode": "Code-barres",
    "Reports.productsList.supplier": "Fournisseur",
    "Reports.productsList.description": "Description",
    "Reports.productsList.image": "Image",
    "Reports.productsList.units": "Unités",
    "Reports.productsList.uom": "UdM",
    "Reports.productsList.retailPrice": "Prix au détail",
    "Reports.productsList.total": "Total",
    "Reports.productsList.noData": "Aucun produit trouvé.",
    "Reports.workHours.teamMember": "Membre de l'équipe",
    "Reports.workHours.date": "Date",
    "Reports.workHours.source": "Source",
    "Reports.workHours.scheduledStart": "Début prévu",
    "Reports.workHours.actualStart": "Début réel",
    "Reports.workHours.startVariance": "Écart début",
    "Reports.workHours.scheduledEnd": "Fin prévue",
    "Reports.workHours.actualEnd": "Fin réelle",
    "Reports.workHours.endVariance": "Écart fin",
    "Reports.workHours.scheduled": "Programmé",
    "Reports.workHours.worked": "Travaillé",
    "Reports.workHours.unpaidBreaks": "Pauses non rémunérées",
    "Reports.workHours.totalPaid": "Total heures payées",
    "Reports.workHours.total": "Total",
    "Reports.workHours.noData": "Aucune entrée de feuille de temps pour cette période.",
    "Reports.workHours.manual": "Manuel",
    "Reports.workHours.punch": "Pointeuse",
    "Reports.breakActivity.teamMember": "Membre de l'équipe",
    "Reports.breakActivity.date": "Date",
    "Reports.breakActivity.source": "Source",
    "Reports.breakActivity.type": "Type",
    "Reports.breakActivity.revenue": "Revenu",
    "Reports.breakActivity.scheduledDuration": "Durée prévue",
    "Reports.breakActivity.actualDuration": "Durée réelle",
    "Reports.breakActivity.total": "Total",
    "Reports.breakActivity.noData": "Aucune activité de pause pour cette période.",
    "Reports.breakActivity.meal": "Repas",
    "Reports.breakActivity.break": "Pause",
    "Reports.breakActivity.unpaid": "Non payée",
    "Reports.breakActivity.manual": "Manuel",
    "Reports.breakActivity.punch": "Pointeuse",
    "Reports.attendance.teamMember": "Membre de l'équipe",
    "Reports.attendance.scheduledShifts": "Plages horaires programmées",
    "Reports.attendance.onTimeStarts": "Débuts à l'heure",
    "Reports.attendance.earlyStarts": "Débuts en avance",
    "Reports.attendance.lateStarts": "Débuts en retard",
    "Reports.attendance.onTimeEnds": "Fins à l'heure",
    "Reports.attendance.earlyEnds": "Fins en avance",
    "Reports.attendance.lateEnds": "Fins en retard",
    "Reports.attendance.punctuality": "Ponctualité",
    "Reports.attendance.missedShifts": "Plages manquées",
    "Reports.attendance.attendanceRate": "Présence",
    "Reports.attendance.total": "Total",
    "Reports.attendance.noData": "Aucune donnée de présence pour cette période.",
    "Reports.salaryDetails.day": "Jour",
    "Reports.salaryDetails.teamMember": "Membre de l'équipe",
    "Reports.salaryDetails.workedHours": "Heures travaillées",
    "Reports.salaryDetails.unpaidBreaks": "Pauses non rémunérées",
    "Reports.salaryDetails.regularPaidHours": "Heures normales rémunérées",
    "Reports.salaryDetails.overtimePaidHours": "Heures supp. rémunérées",
    "Reports.salaryDetails.totalPaidHours": "Total heures payées",
    "Reports.salaryDetails.hourlyRate": "Taux horaire",
    "Reports.salaryDetails.overtimeRate": "Taux heures supp.",
    "Reports.salaryDetails.totalSalary": "Total salaire",
    "Reports.salaryDetails.total": "Total",
    "Reports.salaryDetails.noData": "Aucune donnée salariale pour cette période.",
    "Reports.salarySummary.teamMember": "Membre de l'équipe",
    "Reports.salarySummary.workedHours": "Heures travaillées",
    "Reports.salarySummary.unpaidBreaks": "Pauses non rémunérées",
    "Reports.salarySummary.regularPaidHours": "Heures normales rémunérées",
    "Reports.salarySummary.overtimePaidHours": "Heures supp. rémunérées",
    "Reports.salarySummary.totalPaidHours": "Total heures payées",
    "Reports.salarySummary.hourlyRate": "Taux horaire",
    "Reports.salarySummary.overtimeRate": "Taux heures supp.",
    "Reports.salarySummary.totalSalary": "Total salaire",
    "Reports.salarySummary.total": "Total",
    "Reports.salarySummary.noData": "Aucune donnée salariale pour cette période.",
    "Reports.feeDeductions.teamMember": "Membre de l'équipe",
    "Reports.feeDeductions.deductionName": "Nom de la déduction",
    "Reports.feeDeductions.type": "Type",
    "Reports.feeDeductions.frequency": "Fréquence",
    "Reports.feeDeductions.amount": "Montant",
    "Reports.feeDeductions.effectiveDate": "Date d'effet",
    "Reports.feeDeductions.endDate": "Date de fin",
    "Reports.feeDeductions.status": "Statut",
    "Reports.feeDeductions.total": "Total",
    "Reports.feeDeductions.noData": "Aucune déduction trouvée.",
    "Reports.feeDeductions.recurring": "Récurrente",
    "Reports.feeDeductions.oneTime": "Ponctuelle",
    "Reports.feeDeductions.daily": "Quotidienne",
    "Reports.feeDeductions.revenuePercent": "% Revenus",
    "Reports.feeDeductions.perPay": "Par paie",
    "Reports.feeDeductions.weekly": "Hebdomadaire",
    "Reports.feeDeductions.biweekly": "Bihebdomadaire",
    "Reports.feeDeductions.monthly": "Mensuel",
    "Reports.feeDeductions.perDay": "Par jour",
    "Reports.feeDeductions.perPeriod": "Par période",
    "Reports.feeDeductions.active": "Actif",
    "Reports.feeDeductions.inactive": "Inactif",
    "Reports.feeDeductions.dailyDeduction": "Déduction quotidienne",
    "Reports.feeDeductions.revenueDeduction": "Déduction % revenus",
    "Reports.stockToOrderTitle": "Stock à commander",
    "Reports.stockToOrderDesc": "Produits sous leur seuil bas et quantité à commander pour revenir à la quantité idéale.",
    "Reports.stockToOrder.supplier": "Fournisseur",
    "Reports.stockToOrder.product": "Produit",
    "Reports.stockToOrder.sku": "SKU",
    "Reports.stockToOrder.stock": "Stock",
    "Reports.stockToOrder.threshold": "Seuil",
    "Reports.stockToOrder.ideal": "Idéal",
    "Reports.stockToOrder.sold": "Vendus (période)",
    "Reports.stockToOrder.toOrder": "À commander",
    "Reports.stockToOrder.unitCost": "Coût unitaire",
    "Reports.stockToOrder.estimatedCost": "Coût estimé",
    "Reports.stockToOrder.idealToDefine": "Idéal à définir",
    "Reports.stockToOrder.noLongerToOrder": "Plus à commander",
    "Reports.stockToOrder.noSupplier": "Sans fournisseur",
    "Reports.stockToOrder.products": "{count} produits",
    "Reports.stockToOrder.productsOne": "{count} produit",
    "Reports.stockToOrder.units": "{count} unités",
    "Reports.stockToOrder.unitsOne": "{count} unité",
    "Reports.stockToOrder.partialCost": "coût partiel, {count} produits sans prix coûtant",
    "Reports.stockToOrder.partialCostOne": "coût partiel, {count} produit sans prix coûtant",
    "Reports.stockToOrder.total": "TOTAL",
    "Reports.stockToOrder.noData": "Aucun produit à commander.",
    "Reports.stockToOrder.saved": "Enregistré",
    "Reports.stockToOrder.saveFailed": "La valeur n'a pas pu être enregistrée.",
    "Reports.inventoryPreviewTitle": "Aperçu du rapport d'inventaire",
    "Reports.inventoryPreviewDesc": "Le rapport inclura tous les mouvements de stock (achats, ventes, retours, pertes et ajustements) pour la période sélectionnée, avec un tableau récapitulatif et une liste détaillée chronologique.",
    "Reports.downloadInventoryReport": "Télécharger le rapport d'inventaire",
    "Reports.period": "Période:",
    "Reports.inventory.totalMovements": "Total mouvements",
    "Reports.inventory.valueIn": "Valeur entrée",
    "Reports.inventory.valueOut": "Valeur sortie",
    "Reports.inventory.movementSummary": "Récapitulatif des mouvements",
    "Reports.inventory.productBreakdown": "Produits affectés",
    "Reports.inventory.purchase": "Achat",
    "Reports.inventory.sale": "Vente",
    "Reports.inventory.return": "Retour au fournisseur",
    "Reports.inventory.waste": "Perte",
    "Reports.inventory.internal_use": "Utilisé par le salon",
    "Reports.inventory.adjustment": "Ajustement",
    "Reports.inventory.netChange": "Variation nette",
    "Reports.inventory.product": "Produit",
    "Reports.inventory.sku": "SKU",
    "Reports.inventory.initialStock": "Stock initial",
    "Reports.inventory.finalStock": "Stock final",
    "Reports.inventory.variation": "Variation",
    "Reports.inventory.movements": "Mouvements",
    "Reports.inventory.noMovements": "Aucun mouvement d'inventaire pour la période sélectionnée.",
    "Reports.inventory.type": "Type",
    "Reports.inventory.quantity": "Quantité",
    "Reports.inventory.selectProducts": "Sélectionner les produits",
    "Reports.inventory.selectAll": "Tout sélectionner",
    "Reports.inventory.deselectAll": "Tout désélectionner",
    "Reports.inventory.searchProducts": "Rechercher un produit...",
    "Reports.noAccessTitle": "Accès refusé",
    "Reports.noAccessDescription": "Vous n'avez pas la permission de voir les rapports. Contactez le propriétaire du salon pour demander l'accès.",
    "Reports.noAllowedReports": "Aucun rapport ne vous est ouvert. Adressez-vous au propriétaire du salon.",
    "Reports.snapshotAt": "Instantané au",
    "Reports.print": "Imprimer",

    // Rapports — section vue d'ensemble partagée (chiffres clés, en tableau)
    "Reports.overview.title": "Vue d'ensemble",
    "Reports.overview.measure": "Mesure",
    "Reports.overview.value": "Valeur",
    "Reports.serviceVsRetailTitle": "Service vs Détail",
    "Reports.serviceVsRetailDesc": "Comparer le service et le détail sur les mêmes tickets, et voir qui vend du produit",
    "Reports.serviceVsRetail.noData": "Aucune vente sur cette période",
    "Reports.serviceVsRetail.netHt": "Ventes nettes",
    "Reports.serviceVsRetail.units": "Unités vendues",
    "Reports.serviceVsRetail.tickets": "Tickets",
    "Reports.serviceVsRetail.avgPerOwnTicket": "Moyenne par ticket",
    "Reports.serviceVsRetail.avgPerAllTickets": "Moyenne, tous tickets",
    "Reports.serviceVsRetail.unitsPerOwnTicket": "Unités par ticket",
    "Reports.serviceVsRetail.unitsPerClient": "Unités par client",
    "Reports.serviceVsRetail.avgPerWeek": "Moyenne par semaine",
    "Reports.serviceVsRetail.column.measure": "Mesure",
    "Reports.serviceVsRetail.column.basis": "Base de calcul",
    "Reports.serviceVsRetail.column.service": "Service",
    "Reports.serviceVsRetail.column.retail": "Détail",
    "Reports.serviceVsRetail.column.other": "Autres",
    "Reports.serviceVsRetail.basis.ownTickets": "÷ ses propres tickets",
    "Reports.serviceVsRetail.basis.allTickets": "÷ {count} tickets",
    "Reports.serviceVsRetail.basis.allClients": "÷ {count} clients uniques",
    "Reports.serviceVsRetail.basis.weeks": "÷ {count} semaines",
    "Reports.serviceVsRetail.basis.none": "—",
    "Reports.serviceVsRetail.sideBySide": "Les trois colonnes, côte à côte",
    "Reports.serviceVsRetail.crossRatios": "Ratios croisés",
    "Reports.serviceVsRetail.ticketSplit": "Répartition des tickets",
    "Reports.serviceVsRetail.clientSplit": "Clients uniques par type d'achat",
    "Reports.serviceVsRetail.byEmployee": "Par employé : qui vend du produit ?",
    "Reports.serviceVsRetail.ratio.avgTicketAll": "Ticket moyen (service et détail)",
    "Reports.serviceVsRetail.ratio.retailPerServiceTicket": "Détail par ticket de service",
    "Reports.serviceVsRetail.ratio.retailPerServiceClient": "Détail par client de service",
    "Reports.serviceVsRetail.ratio.retailUnitsPerServiceTicket": "Unités détail par ticket de service",
    "Reports.serviceVsRetail.ratio.retailUnitsPerServiceClient": "Unités détail par client de service",
    "Reports.serviceVsRetail.ratio.pctClientsBuyingRetail": "% de clients achetant du détail",
    "Reports.serviceVsRetail.ratio.pctRetailOverService": "% détail par rapport au service",
    "Reports.serviceVsRetail.ratio.pctRetailOverTotal": "% détail par rapport aux ventes totales",
    "Reports.serviceVsRetail.split.total": "Total",
    "Reports.serviceVsRetail.split.serviceOnly": "Service seulement",
    "Reports.serviceVsRetail.split.retailOnly": "Détail seulement",
    "Reports.serviceVsRetail.split.both": "Service et détail",
    "Reports.serviceVsRetail.split.otherOnly": "Autres seulement",
    "Reports.serviceVsRetail.split.count": "Nb",
    "Reports.serviceVsRetail.split.clients": "Clients",
    "Reports.serviceVsRetail.split.percent": "%",
    "Reports.serviceVsRetail.employee.name": "Employé",
    "Reports.serviceVsRetail.employee.serviceNetHt": "Services $",
    "Reports.serviceVsRetail.employee.retailNetHt": "Détail $",
    "Reports.serviceVsRetail.employee.pctRetailOverService": "Détail / service",
    "Reports.serviceVsRetail.employee.retailPerServiceTicket": "Détail / ticket serv.",
    "Reports.serviceVsRetail.employee.pctClientsBuyingRetail": "% clients détail",
    "Reports.serviceVsRetail.note.amountsExclTax": "Les montants sont hors taxes.",
    "Reports.serviceVsRetail.note.noCustomer": "Les factures sans client rattaché sont exclues des lignes par client.",

    // Rapports — en-tête unifié
    "Reports.tileAvailable": "Rapports disponibles",
    "Reports.tileCategories": "Catégories",
    "Reports.tileInDevelopment": "En développement",
    "Reports.tileEnterprise": "Enterprise",
    "Reports.enterpriseBadge": "Enterprise",
    "Reports.variablePriceExcluded": "{count} rendez-vous à prix variable, non pris en compte",
  },
};
